import { AuthManager } from './services/AuthManager';
import { BattlePassService } from './services/BattlePassService';
// 簡化版多國語言系統
import { SimpleI18n } from './i18n/SimpleI18n.js';
import { LangSwitcher } from './i18n/LangSwitcher.js';
import { config, shouldLog } from './config/config';
import { UserData, Task, Reward, RewardGroup, RewardItem, ApiReward, ApiRewardItem } from './types/battlepass';

/**
 * 通行證配置
 */
const PASS_CONFIG = {
  MAX_LEVEL: 60,
  STARS_PER_LEVEL: 150,
  EXTRA_BONUS_POINTS: 500,
};

/**
 * 任務狀態
 */
const TASK_STATUS = {
  INPROGRESS: 'inprogress',
  COMPLETED: 'completed',
  CLAIMED: 'claimed',
};

/**
 * 用戶數據管理器
 */
class UserDataManager {
  private data: UserData = {
    stars: 0,
    level: 0,
    diamonds: 0,
    hasSwagPass: false,
    claimableRewards: 0,
    tasks: [],
    rewards: [],
  };

  updateData(newData: Partial<UserData>): void {
    this.data = { ...this.data, ...newData };
  }

  getData(): UserData {
    return this.data;
  }

  resetData(): void {
    this.data = {
      stars: 0,
      level: 0,
      diamonds: 0,
      hasSwagPass: false,
      claimableRewards: 0,
      tasks: [],
      rewards: [],
    };
  }

}

/**
 * SWAG Battle Pass 主應用程式
 */
class BattlePassApp {
  private authManager: AuthManager
  private rewardAccessibility: { [group: string]: boolean } = {} // Track reward group accessibility;
  private battlePassService: BattlePassService | null = null;
  private userDataManager: UserDataManager;
  // 簡化版多國語言系統
  private simpleI18n: SimpleI18n;
  private langSwitcher: LangSwitcher;
  private isInitialized: boolean = false;
  private isEmbeddedMode: boolean = false; // 是否在 iframe 中運行（嵌入模式）

  constructor() {
    this.authManager = AuthManager.getInstance();
    this.userDataManager = new UserDataManager();
    // 簡化版多國語言初始化
    this.simpleI18n = SimpleI18n.getInstance();
    this.langSwitcher = new LangSwitcher();
    this.setupEventListeners();
    this.setupInfoModal();
    this.initialize();
  }

  private async initialize(): Promise<void> {
    try {
      // 🔖 版本資訊日誌
      const version = '2024.12.18-72'; // 手動更新版本號
      const buildTime = new Date().toISOString();
      console.log(`%c🚀 Battle Pass Application`, 'color: #00ff00; font-weight: bold; font-size: 14px;');
      console.log(`%c📦 Version: ${version}`, 'color: #00aaff; font-weight: bold;');
      console.log(`%c⏰ Page Load: ${buildTime}`, 'color: #00aaff;');
      console.log(`%c🌍 Environment: ${import.meta.env.MODE || 'production'}`, 'color: #00aaff;');
      console.log(`%c🔗 Base URL: ${config.api.baseUrl}`, 'color: #00aaff;');
      console.log('%c' + '='.repeat(60), 'color: #666;');
      
      console.log('[BattlePassApp] 開始初始化應用程式');
      
      // 檢查是否在 iframe 中運行（嵌入模式）
      this.isEmbeddedMode = window.parent !== window;

      // 設置初始狀態指示器
      this.updateAppStatus('initializing');

      // 等待認證（在嵌入模式下）或直接初始化（standalone模式）
      if (this.isEmbeddedMode) {
        console.log('[BattlePassApp] 嵌入模式：等待 token');
        await this.waitForAuthentication();
      } else {
        console.log('[BattlePassApp] Standalone 模式：直接初始化服務');
        // 在 standalone 模式下，直接初始化服務
      }

      // 初始化 Battle Pass 服務
      const token = this.authManager.getToken();
      this.battlePassService = new BattlePassService(config.api.baseUrl, token || undefined);
      console.log('[BattlePassApp] BattlePassService 已初始化', { hasToken: !!token });

      // 載入 API 數據
      console.log('[BattlePassApp] 載入 API 數據');
        await this.loadApiData();

      // 渲染 UI
      console.log('[BattlePassApp] 開始渲染 UI');
      this.renderUI();
      this.isInitialized = true;
      this.updateAppStatus('ready');

      // 設置初始頁面狀態（默認顯示獎勵頁面）
      this.setInitialPageState();

      // 更新彈窗中的版本號
      this.updateModalVersion(version);

      console.log('[BattlePassApp] 應用程式初始化完成');
    } catch (error) {
        console.error('[BattlePassApp] 應用程式初始化失敗：', error);
      this.updateAppStatus('error');
      
      // 顯示錯誤信息
      const errorDiv = document.createElement('div');
      errorDiv.style.cssText = `
        position: fixed;
        top: 50px;
        left: 20px;
        background: orange;
        color: black;
        padding: 10px;
        border-radius: 5px;
        z-index: 9999;
        font-family: monospace;
      `;
      errorDiv.textContent = `請登入 SWAG 帳號來體驗 Battle Pass 功能`;
      document.body.appendChild(errorDiv);
    }
  }

  private waitForAuthentication(): Promise<void> {
    return new Promise((resolve, reject) => {
      if (this.authManager.isAuthenticated()) {
        resolve();
        return;
      }

      const timeout = setTimeout(() => {
        reject(new Error('認證超時'));
      }, 30000);

      this.authManager.onTokenChange(() => {
        clearTimeout(timeout);
        if (shouldLog('info')) {
          console.log('[BattlePassApp] 認證成功，Token 已接收');
        }
        resolve();
      });
    });
  }

  private async loadApiData(): Promise<void> {
    if (!this.battlePassService) return;

    try {
      // 🔍 DEBUG: 顯示 API 基礎 URL 以確認使用哪個環境
      console.log('🔍 [API DEBUG] 使用的 API 基礎 URL:', this.battlePassService.getBaseUrl());
      console.log('🔍 [API DEBUG] 當前時間戳:', new Date().toISOString());
      
      // 從 API 獲取數據（恢復使用 points API 以確保安全性）
      const [points, dailyQuests, monthlyFreeRewards, monthlyPaidRewards] = await Promise.all([
        this.battlePassService.getPoints(),
        this.battlePassService.getQuests('DAY'), // 每日任務
        this.battlePassService.getRewards('MONTH', 'free'), // 月度免費獎勵
        this.battlePassService.getRewards('MONTH', 'paid'), // 月度付費獎勵
      ]);

      // 合併免費和付費獎勵
      const monthlyRewards = [monthlyFreeRewards, monthlyPaidRewards];

      // 儲存獎勵可訪問性信息
      this.rewardAccessibility = {
        'MONTH_free': monthlyFreeRewards.is_accessible ?? true,
        'MONTH_paid': monthlyPaidRewards.is_accessible ?? false,
      };

      // 將 quests 轉換為 tasks 格式
      const tasks = this.convertQuestsToTasks(dailyQuests);
      const passLevelRewards = this.transformRewardGroups(monthlyRewards);

      // 🔧 [跨設備修復] 完全依賴 API 積分，確保跨設備一致性
      const finalStars = points.points; // 直接使用 API 積分
      const finalLevel = this.calculateUserLevel(finalStars);
      
      console.log('🔧 [跨設備修復] loadApiData - 使用純 API 積分:', finalStars, '等級:', finalLevel);

      // 更新用戶數據
      this.userDataManager.updateData({
        stars: finalStars, // 🔧 跨設備修復：純 API 積分
        level: finalLevel,
        tasks: tasks,
        rewards: passLevelRewards,
      });

      if (shouldLog('info')) {
        console.log('[BattlePassApp] API 數據載入完成', {
          points: points.points,
          tasks: tasks.length,
          dailyQuests: dailyQuests.quests.length,
          monthlyRewards: monthlyRewards.length,
          apiUrl: config.api.baseUrl,
        });
      }
    } catch (error) {
      if (shouldLog('error')) {
        console.error('[BattlePassApp] 載入 API 數據失敗：', error);
      }
      
      // 在開發環境下，API 失敗通常是正常的（使用 mock server）
      if (shouldLog('error')) {
        console.error('[BattlePassApp] API 載入失敗，請確認 mock server 是否運行');
        console.error('請執行: npm run dev:server 啟動 mock server');
      }
      
      // 設置空的默認數據，避免應用崩潰
      this.userDataManager.updateData({
        stars: 0,
        level: 1,
        tasks: [],
        rewards: [],
      });
      
      throw error;
    }
  }



  /**
   * 從 target 推算對應的等級
   * 基於戰鬥通行證的等級系統：每級 150 點
   */
  private calculateLevelFromTarget(target: number): number {
    // 處理特殊情況（如 500 點的額外獎勵）
    if (target === 500) return 0; // 特殊獎勵不對應特定等級
    
    // 基本計算：每級 150 點
    return Math.ceil(target / 150);
  }

  private transformRewardGroups(groups: RewardGroup[]): Reward[] {
    const rewardsByLevel: { [level: number]: Reward } = {};

    groups.forEach(group => {
      group.rewards.forEach(reward => {
        const level = this.calculateLevelFromTarget(reward.target);
        
        // 跳過特殊獎勵（level 0）
        if (level === 0) return;
        
        if (!rewardsByLevel[level]) {
          rewardsByLevel[level] = {
            level: level,
            free: null,
            paid: null,
          };
        }

        // Process API reward and convert to internal format
        const processedMetadata = this.processRewardMetadata(reward.item.type, reward.item.metadata, level, reward.item);
        const rewardItem: RewardItem & { claimed: boolean } = {
          id: reward.item.id,
          type: reward.item.type,
          metadata: processedMetadata,
          claimed: reward.claimed !== null && reward.claimed !== undefined,
        };

        if (group.type === 'free') {
          if (!rewardsByLevel[level].free) {
            rewardsByLevel[level].free = [];
          }
          rewardsByLevel[level].free!.push(rewardItem);
        } else if (group.type === 'paid') {
          if (!rewardsByLevel[level].paid) {
            rewardsByLevel[level].paid = [];
          }
          rewardsByLevel[level].paid!.push(rewardItem);
        }
      });
    });

    return Object.values(rewardsByLevel).sort((a, b) => a.level - b.level);
  }

  private convertQuestsToTasks(dailyQuests: unknown): Task[] {
    const tasks: Task[] = [];

    // 類型守衛函數 - 更新以匹配新的 API 結構
    const isQuestGroup = (
      obj: unknown
    ): obj is { id: string; group: string; quests: Array<{ id: string; points: number; link: string; conditions: Array<{ threshold: number; unit: string }>; completed?: number }> } => {
      return (
        typeof obj === 'object' && 
        obj !== null && 
        'quests' in obj && 
        Array.isArray((obj as { quests: unknown }).quests)
      );
    };

    // 🔧 [跨設備修復] 移除每日任務重置邏輯，完全依賴 API 狀態
    if (isQuestGroup(dailyQuests) && dailyQuests.group === 'DAY') {
      console.log('[BattlePassApp] 🔧 跨設備修復：檢測到每日任務，任務組 ID:', dailyQuests.id);
      console.log('[BattlePassApp] 🔧 跨設備修復：不再處理 localStorage 重置，完全依賴 API 狀態');
    }

    // 🔧 [跨設備修復] 移除 localStorage 依賴，完全依賴 API 狀態
    console.log('[BattlePassApp] 🔧 跨設備修復：移除 localStorage 依賴，使用純 API 狀態');

    // 轉換每日任務
    if (isQuestGroup(dailyQuests)) {
      dailyQuests.quests.forEach(quest => {
        // 從 conditions 中獲取目標值
        const condition = quest.conditions?.[0];
        const target = condition?.threshold || 1;
        
        // 🔧 修復：任務狀態完全依賴 API，不使用 localStorage
        const isCompleted = typeof quest.completed === 'number';
        
        console.log(`[BattlePassApp] 🔧 任務 ${quest.id}: completed=${isCompleted} (純 API 狀態)`);
        
        // 簡化狀態邏輯：只有進行中和已完成兩種狀態
        let taskStatus = TASK_STATUS.INPROGRESS;
        if (isCompleted) {
          taskStatus = TASK_STATUS.COMPLETED; // 有 completed 欄位就是可領取
        }
        
        tasks.push({
          id: quest.id,
          title: this.getQuestTitle(quest.id),
          description: this.getQuestDescription(quest.id),
          reward: quest.points, // 使用 API 原始積分
          status: taskStatus,
          type: 'daily',
          progress: {
            current: isCompleted ? target : 0, // 🔧 修復：移除 isClaimed 引用
            target: target,
          },
          link: quest.link,
          completed: quest.completed, // 🔧 修復：傳遞 API 的 completed 時間戳供排序使用
        });
      });
    }

    return tasks;
  }

  private getQuestTitle(questId: string): string {
    const titles: { [key: string]: string } = {
      daily_login: '每日登入',
      stream_view_duration: '觀看直播5分鐘',
      spend_diamond_amount: '消費1000鑽石',
      send_livestream_chat_count: '在直播間傳送訊息',
      send_livestream_gift_count: '在直播間送禮',
      send_chat_message_or_gift_count: '在私訊或群聊中傳送訊息或禮物',
      unlock_post_count: '解鎖限時動態或貼文',
      purchase_diamond_pack_count: '購買鑽石',
      watch_shorts_count: '觀看5則短影片',
      send_livestream_karaoke_count: '在直播間使用指令或玩具',
      bet_livestream_game_count: '在直播間玩遊戲',
      monthly_spend: '月度消費',
    };
    return titles[questId] || questId;
  }

  private getQuestDescription(questId: string): string {
    const descriptions: { [key: string]: string } = {
      daily_login: '領取登入獎勵',
      stream_view_duration: '累計觀看直播5分鐘',
      spend_diamond_amount: '累計消費1000鑽石',
      send_livestream_chat_count: '在任何直播間傳送一則訊息',
      send_livestream_gift_count: '在任何直播間送出一個禮物',
      send_chat_message_or_gift_count: '在私訊或群聊中傳送訊息或禮物',
      unlock_post_count: '解鎖任何付費限時動態或貼文',
      purchase_diamond_pack_count: '完成一筆鑽石購買',
      watch_shorts_count: '觀看5則不同的短影片',
      send_livestream_karaoke_count: '在直播間使用一次指令或玩具',
      bet_livestream_game_count: '在直播間玩一場遊戲',
      monthly_spend: '本月累計消費 1000 鑽石',
    };
    return descriptions[questId] || '完成指定任務';
  }

  private setupInfoModal(): void {
    if (shouldLog('debug')) {
      console.log('[BattlePassApp] 設置注意事項彈窗...');
    }

    const infoButton = document.querySelector('.info-button');
    const modal = document.getElementById('info-modal');
    const closeButton = document.querySelector('.modal-close');

    if (!infoButton || !modal || !closeButton) {
      if (shouldLog('warn')) {
        console.warn('[BattlePassApp] 找不到注意事項彈窗相關元素');
      }
      return;
    }

    // 點擊 "?" 按鈕開啟彈窗
    infoButton.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      modal.classList.add('show');
      modal.setAttribute('aria-hidden', 'false');
    });

    // 點擊關閉按鈕關閉彈窗
    closeButton.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      modal.classList.remove('show');
      modal.setAttribute('aria-hidden', 'true');
    });

    // 點擊彈窗背景關閉彈窗
    modal.addEventListener('click', (e) => {
      if (e.target === modal) {
        modal.classList.remove('show');
        modal.setAttribute('aria-hidden', 'true');
      }
    });

    // ESC 鍵關閉彈窗
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && modal.classList.contains('show')) {
        modal.classList.remove('show');
        modal.setAttribute('aria-hidden', 'true');
      }
    });

    if (shouldLog('debug')) {
      console.log('[BattlePassApp] 注意事項彈窗設置完成');
    }
  }

  private showInfoModal(): void {
    const modal = document.getElementById('info-modal');
    if (modal) {
      modal.classList.add('show');
      modal.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden'; // 防止背景滾動
      
      if (shouldLog('debug')) {
        console.log('[BattlePassApp] 顯示注意事項彈窗');
      }
    }
  }

  private hideInfoModal(): void {
    const modal = document.getElementById('info-modal');
    if (modal) {
      modal.classList.remove('show');
      modal.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = ''; // 恢復背景滾動
      
      if (shouldLog('debug')) {
        console.log('[BattlePassApp] 隱藏注意事項彈窗');
      }
    }
  }

  private setInitialPageState(): void {
    // 檢查當前哪個頁面是 active 的
    const rewardTab = document.querySelector('.tab-button[aria-controls="pass-level-content"]') as HTMLElement;
    const taskTab = document.querySelector('.tab-button[aria-controls="daily-reward-content"]') as HTMLElement;
    const rewardContent = document.getElementById('pass-level-content');
    const taskContent = document.getElementById('daily-reward-content');
    
    console.log('[BattlePassApp] 🚀 強制設置初始頁面狀態...');
    console.log('[BattlePassApp] 頁面狀態檢查:', {
      rewardTabActive: rewardTab?.classList.contains('active'),
      taskTabActive: taskTab?.classList.contains('active'),
      rewardContentActive: rewardContent?.classList.contains('active'),
      taskContentActive: taskContent?.classList.contains('active')
    });
    
    // 獲取按鈕元素
    const rewardClaimBtn = document.querySelector('.pass-level-claim') as HTMLElement;
    const taskRefreshBtn = document.querySelector('.task-refresh-button') as HTMLElement;
    
    // 🔍 調試：檢查按鈕是否存在
    console.log('[BattlePassApp] 🔍 按鈕元素檢查:', {
      rewardClaimBtn: !!rewardClaimBtn,
      taskRefreshBtn: !!taskRefreshBtn,
      rewardBtnText: rewardClaimBtn?.querySelector('.button-text')?.textContent,
      taskBtnText: taskRefreshBtn?.querySelector('.button-text')?.textContent
    });
    
    // 根據當前 active 狀態設置正確的 body 類別和按鈕顯示
    if (taskContent?.classList.contains('active')) {
      // 任務頁面是 active 的
      document.body.classList.add('task-page-active');
      document.body.classList.remove('reward-page-active');
      
      // 💀 暴力設置按鈕狀態
      if (rewardClaimBtn) {
        rewardClaimBtn.style.cssText = 'display: none !important;';
        console.log('[BattlePassApp] 💀 暴力隱藏獎勵按鈕');
      }
      if (taskRefreshBtn) {
        taskRefreshBtn.style.cssText = 'display: inline-flex !important;';
        console.log('[BattlePassApp] 💀 暴力顯示任務按鈕');
      }
      
      console.log('[BattlePassApp] 🎯 初始狀態：任務頁面 active，設置 task-page-active');
    } else {
      // 默認或獎勵頁面是 active 的
      document.body.classList.add('reward-page-active');
      document.body.classList.remove('task-page-active');
      
      // 💀 暴力設置按鈕狀態
      if (rewardClaimBtn) {
        rewardClaimBtn.style.cssText = 'display: inline-flex !important;';
        console.log('[BattlePassApp] 💀 暴力顯示獎勵按鈕');
      }
      if (taskRefreshBtn) {
        taskRefreshBtn.style.cssText = 'display: none !important;';
        console.log('[BattlePassApp] 💀 暴力隱藏任務按鈕');
      }
      
      console.log('[BattlePassApp] 🎯 初始狀態：獎勵頁面 active，設置 reward-page-active');
    }
    
    console.log('[BattlePassApp] 💀 暴力設置完成！最終按鈕狀態:', {
      rewardDisplay: rewardClaimBtn?.style.display || 'not found',
      taskDisplay: taskRefreshBtn?.style.display || 'not found'
    });
  }

  private setupEventListeners(): void {
    // 監聽認證狀態變化
    this.authManager.onTokenChange(() => {
      if (!this.isInitialized || !this.isEmbeddedMode) return;

      this.loadApiData().catch(error => {
        if (shouldLog('error')) {
          console.error('[BattlePassApp] 重新載入數據失敗：', error);
        }
        this.showError('數據更新失敗');
      });
    });

    // 監聽語言變更事件
    window.addEventListener('languageChanged', ((event: CustomEvent) => {
      if (!this.isInitialized) return;
      
      console.log(`[BattlePassApp] 語言已變更為: ${event.detail.language}`);
      
      try {
        // 重新渲染任務列表以更新翻譯
        console.log('[BattlePassApp] 開始重新渲染任務列表...');
        this.renderTaskList();
        
        // 檢查當前是否在獎勵頁面，如果是則重新渲染獎勵區域
        const rewardsTab = document.querySelector('.tab-button:first-child') as HTMLElement;
        const isRewardsTabActive = rewardsTab?.getAttribute('aria-selected') === 'true';
        
        if (isRewardsTabActive) {
          console.log('[BattlePassApp] 當前在獎勵頁面，開始重新渲染獎勵區域...');
          this.renderPassLevelRewards();
        } else {
          console.log('[BattlePassApp] 當前不在獎勵頁面，獎勵區域將在切換到獎勵頁面時更新');
        }
        
        // 延遲更新靜態翻譯，避免覆蓋動態內容
        setTimeout(() => {
          console.log('[BattlePassApp] 開始更新靜態頁面翻譯...');
          this.simpleI18n.updatePageTexts();
          console.log('[BattlePassApp] 靜態頁面翻譯已更新');
        }, 200);
        
        console.log('[BattlePassApp] 語言切換後重新渲染完成');
      } catch (error) {
        console.error('[BattlePassApp] 語言切換處理錯誤:', error);
      }
    }) as EventListener);

    // 監聽 UI 事件
    document.addEventListener('click', this.handleClick.bind(this));
  }

  private handleClick(event: Event): void {
    const target = event.target as HTMLElement;

    console.log('[BattlePassApp] 🔍 點擊事件:', {
      tagName: target.tagName,
      className: target.className,
      textContent: target.textContent?.trim(),
      hasTabButton: target.classList.contains('tab-button'),
      closestTabButton: !!target.closest('.tab-button')
    });

    // 更精確的事件處理，避免空白區域觸發不相關的事件
    if (target.classList.contains('tab-button') || target.closest('.tab-button')) {
      event.preventDefault();
      const tabButton = target.classList.contains('tab-button') ? target : target.closest('.tab-button') as HTMLElement;
      console.log('[BattlePassApp] 🎯 觸發頁籤切換:', tabButton.textContent?.trim());
      this.handleTabSwitch(tabButton);
    } else if (target.classList.contains('claim-all-button') || target.closest('.claim-all-button')) {
      event.preventDefault();
      const button = target.classList.contains('claim-all-button') ? target : target.closest('.claim-all-button') as HTMLElement;
      
      // 檢查是否為任務更新按鈕
      if (button.classList.contains('task-refresh-button')) {
        this.refreshTaskProgress();
      } else {
        this.handleClaimAll(button);
      }
    } else if (target.classList.contains('premium-pass-button') || target.closest('.premium-pass-button')) {
      event.preventDefault();
      this.handlePremiumPassPurchase();
    } else if (target.classList.contains('reward-item') && !target.classList.contains('empty')) {
      event.preventDefault();
      this.handleRewardClick(target);
    } else if (target.closest('.reward-item') && !target.closest('.reward-item')?.classList.contains('empty')) {
      // 處理點擊獎勵卡片內部元素的情況
      event.preventDefault();
      const rewardItem = target.closest('.reward-item') as HTMLElement;
      this.handleRewardClick(rewardItem);
    } else if (target.classList.contains('go-button') || target.closest('.go-button')) {
      // 只處理按鈕點擊
      event.preventDefault();
      const taskItem = target.closest('.task-item') as HTMLElement;
      if (taskItem) {
        this.handleTaskClick(taskItem);
      }
    }
    // 完全移除任務項目的通用點擊處理，避免誤觸
  }

  private handleTabSwitch(tabButton: HTMLElement): void {
    console.log('[BattlePassApp] 頁籤切換:', tabButton.textContent);

    // 移除所有 active 狀態
    document.querySelectorAll('.tab-button').forEach(btn => {
      btn.classList.remove('active');
      btn.setAttribute('aria-selected', 'false');
    });
    document.querySelectorAll('.tab-content').forEach(content => {
      content.classList.remove('active');
    });

    // 設置當前標籤為 active
    tabButton.classList.add('active');
    tabButton.setAttribute('aria-selected', 'true');

    // 根據按鈕位置或屬性確定要顯示的內容（不依賴文字內容）
    const isRewardTab = tabButton.getAttribute('aria-controls') === 'pass-level-content' || 
                       tabButton === document.querySelector('.tab-button:first-child');
    const isTaskTab = tabButton.getAttribute('aria-controls') === 'daily-reward-content' ||
                     tabButton === document.querySelector('.tab-button:last-child');
    
    console.log('[BattlePassApp] 頁籤類型:', { isRewardTab, isTaskTab });

    if (isRewardTab) {
      // 顯示獎勵頁面
      const rewardContent = document.getElementById('pass-level-content');
      if (rewardContent) {
        rewardContent.classList.add('active');
        console.log('[BattlePassApp] 顯示獎勵頁面');
        
        // CSS 已自動處理按鈕顯示邏輯 + 添加 data 屬性作為備用
        console.log('[BattlePassApp] 獎勵頁面 - 顯示一鍵領取按鈕，隱藏更新進度按鈕');
        
        // 添加 data 屬性確保按鈕正確顯示（備用方案）
        const rewardClaimBtn = document.querySelector('.pass-level-claim') as HTMLElement;
        const taskRefreshBtn = document.querySelector('.task-refresh-button') as HTMLElement;
        
        console.log('[BattlePassApp] 🔍 獎勵頁面按鈕檢查:', {
          rewardClaimBtn: !!rewardClaimBtn,
          taskRefreshBtn: !!taskRefreshBtn,
          rewardClaimDisplay: rewardClaimBtn?.style.display || 'not set',
          taskRefreshDisplay: taskRefreshBtn?.style.display || 'not set',
          activeContent: document.querySelector('#pass-level-content.active') ? 'pass-level-content' : 'none'
        });
        
        // ✅ 正確的按鈕切換邏輯
        console.log('[BattlePassApp] 🎯 獎勵頁面：顯示獎勵按鈕，隱藏更新進度按鈕');
        
        // 設置 body 類別讓 CSS 規則生效
        document.body.classList.add('reward-page-active');
        document.body.classList.remove('task-page-active');
        console.log('[BattlePassApp] ✅ 設置 body.reward-page-active 類別');
        
        // 💀 暴力顯示獎勵按鈕
        if (rewardClaimBtn) {
          rewardClaimBtn.style.cssText = 'display: inline-flex !important;';
          console.log('[BattlePassApp] 💀 暴力顯示獎勵按鈕');
        }
        
        // 💀 暴力隱藏任務按鈕
        if (taskRefreshBtn) {
          taskRefreshBtn.style.cssText = 'display: none !important;';
          console.log('[BattlePassApp] 💀 暴力隱藏任務按鈕');
        }
        
        console.log('[BattlePassApp] 📋 獎勵頁面按鈕設置完成');
        
        // 重新渲染獎勵區域以確保翻譯正確（如果之前在其他頁面切換了語言）
        console.log('[BattlePassApp] 切換到獎勵頁面，重新渲染獎勵區域...');
        this.renderPassLevelRewards();
        
        // 自動滾動到當前等級（延遲執行確保標籤切換動畫完成）
        setTimeout(() => {
          this.scrollToCurrentLevel();
        }, 300);
      }
    } else if (isTaskTab) {
      // 顯示任務頁面
      const taskContent = document.getElementById('daily-reward-content');
      if (taskContent) {
        taskContent.classList.add('active');
        console.log('[BattlePassApp] 顯示任務頁面');
        
        // 當切換到任務頁面時，再次清理
        this.cleanupTaskPage();
        
        // CSS 已自動處理按鈕顯示邏輯 + 添加 data 屬性作為備用
        console.log('[BattlePassApp] 任務頁面 - 顯示更新進度按鈕，隱藏一鍵領取按鈕');
        
        // 添加 data 屬性確保按鈕正確顯示（備用方案）
        const rewardClaimBtn = document.querySelector('.pass-level-claim') as HTMLElement;
        const taskRefreshBtn = document.querySelector('.task-refresh-button') as HTMLElement;
        
        console.log('[BattlePassApp] 🔍 任務頁面按鈕檢查:', {
          rewardClaimBtn: !!rewardClaimBtn,
          taskRefreshBtn: !!taskRefreshBtn,
          rewardClaimDisplay: rewardClaimBtn?.style.display || 'not set',
          taskRefreshDisplay: taskRefreshBtn?.style.display || 'not set',
          activeContent: document.querySelector('#daily-reward-content.active') ? 'daily-reward-content' : 'none',
          rewardBtnText: rewardClaimBtn?.querySelector('.button-text')?.textContent,
          taskBtnText: taskRefreshBtn?.querySelector('.button-text')?.textContent,
          rewardBtnVisible: rewardClaimBtn ? window.getComputedStyle(rewardClaimBtn).display : 'not found',
          taskBtnVisible: taskRefreshBtn ? window.getComputedStyle(taskRefreshBtn).display : 'not found'
        });
        
        // ✅ 正確的按鈕切換邏輯
        console.log('[BattlePassApp] 🎯 任務頁面：隱藏獎勵按鈕，顯示更新進度按鈕');
        
        // 設置 body 類別讓 CSS 規則生效
        document.body.classList.add('task-page-active');
        document.body.classList.remove('reward-page-active');
        console.log('[BattlePassApp] ✅ 設置 body.task-page-active 類別');
        
        // 💀 暴力隱藏獎勵按鈕
        if (rewardClaimBtn) {
          rewardClaimBtn.style.cssText = 'display: none !important;';
          console.log('[BattlePassApp] 💀 暴力隱藏獎勵按鈕');
        }
        
        // 💀 暴力顯示任務按鈕
        if (taskRefreshBtn) {
          taskRefreshBtn.style.cssText = 'display: inline-flex !important;';
          console.log('[BattlePassApp] 💀 暴力顯示任務按鈕');
        }
        
        // 🔍 暴力設置後的最終檢查
        console.log('[BattlePassApp] 🔍 暴力設置後最終狀態:', {
          rewardFinalDisplay: rewardClaimBtn ? window.getComputedStyle(rewardClaimBtn).display : 'not found',
          taskFinalDisplay: taskRefreshBtn ? window.getComputedStyle(taskRefreshBtn).display : 'not found',
          rewardStyleDisplay: rewardClaimBtn?.style.display || 'not set',
          taskStyleDisplay: taskRefreshBtn?.style.display || 'not set'
        });
        
        console.log('[BattlePassApp] 📋 任務頁面按鈕設置完成');
      }
    }

    this.updateNotificationDots();
    
    // 標籤頁切換後，延遲更新翻譯以確保新內容已渲染
    setTimeout(() => {
      this.simpleI18n.updatePageTexts();
      console.log('[BattlePassApp] 標籤頁切換後翻譯已更新');
    }, 300); // 增加延遲時間確保內容完全渲染
  }

  private async handleClaimAll(button: HTMLElement): Promise<void> {
    const isPassLevel = button.classList.contains('pass-level-claim');

    // 🔄 自動給分模式：只處理獎勵一鍵領取
    if (isPassLevel) {
      this.claimAllPassRewards();
    }
  }

  private handlePremiumPassPurchase(): void {
    this.showPurchaseDialog();
  }

  private handleRewardClick(rewardElement: HTMLElement): void {
    const level = parseInt(rewardElement.dataset.level || '0');
    
    // 檢查是否為鑽石獎勵（付費獎勵）
    const isDiamondReward = rewardElement.classList.contains('swag-pass-reward');
    
    if (isDiamondReward) {
      // 鑽石獎勵點擊邏輯
      const isPaidAccessible = this.rewardAccessibility['MONTH_paid'] ?? false;
      
      if (!isPaidAccessible) {
        // 未購買 Pass → 顯示購買彈窗
        console.log('[BattlePassApp] 點擊鑽石獎勵，未購買 Pass，顯示購買彈窗');
        this.showPurchaseDialog();
        return;
      } else {
        // 已購買 Pass → 無任何行為（靜態展示）
        console.log('[BattlePassApp] 點擊鑽石獎勵，已購買 Pass，無操作');
        return;
      }
    } else {
      // 免費獎勵保持原有邏輯（通過一鍵領取）
      // 實際上免費獎勵也不應該有個別點擊行為，但保持兼容性
      console.log('[BattlePassApp] 點擊免費獎勵，保持原有行為');
    if (level > 0) {
      this.claimReward(rewardElement, level);
      }
    }
  }

  private handleTaskClick(taskElement: HTMLElement): void {
    const taskId = taskElement.dataset.taskId;
    const button = taskElement.querySelector('.go-button') as HTMLElement;
    
    if (!taskId || !button) return;

    const userData = this.userDataManager.getData();
    const tasks = userData.tasks || [];
    const task = tasks.find((t: Task) => t.id === taskId);

    if (!task) return;

    // 🔄 自動給分模式：根據按鈕狀態執行不同的動作
    if (button.classList.contains('claimed')) {
      // 已完成狀態 - 顯示提示訊息
      this.showInfo(this.simpleI18n.translate('此任務已經完成了'));
    } else if (button.classList.contains('goto')) {
      // 前往狀態 - 處理頁面跳轉
      this.handleTaskNavigation(task, button);
    }
  }

  // 🔄 自動給分模式：移除任務領取功能（已由後端自動處理）

  private handleTaskNavigation(task: Task, button: HTMLElement): void {
    // 使用 task.link 進行跳轉
    if (task.link) {
      console.log(`[BattlePassApp] 前往完成任務: ${task.description} -> ${task.link}`);
      
      // 顯示跳轉提示
      this.showInfo(`正在前往完成任務: ${task.description}`);
      
      // 在新視窗開啟任務頁面
      try {
        window.open(task.link, '_blank', 'noopener,noreferrer');
        console.log(`[BattlePassApp] 成功開啟任務連結: ${task.link}`);
      } catch (error) {
        console.error('[BattlePassApp] 開啟任務連結失敗:', error);
        this.showError(this.simpleI18n.translate('無法開啟任務頁面，請手動前往'));
      }
    } else {
      console.warn(`[BattlePassApp] 任務 ${task.id} 缺少跳轉連結`);
      this.showWarning(this.simpleI18n.translate('此任務暫無跳轉頁面'));
    }
  }

  private simulateTaskProgress(task: Task): void {
    // 模擬任務進度（僅用於開發測試）
    const increment = Math.min(Math.floor(Math.random() * 3) + 1, task.progress.target - task.progress.current);
    task.progress.current = Math.min(task.progress.current + increment, task.progress.target);

    if (task.progress.current >= task.progress.target) {
      task.status = TASK_STATUS.COMPLETED;
      this.showSuccess('任務完成！可以領取獎勵了');
    } else {
      this.showInfo(`任務進度更新：${task.progress.current}/${task.progress.target}`);
    }

    const userData = this.userDataManager.getData();
    this.userDataManager.updateData({ tasks: userData.tasks });
    this.renderTaskList();
    this.updateNotificationDots();
  }

  private renderUI(): void {
    try {
      console.log('[BattlePassApp] 開始渲染 UI 組件');
      
      console.log('[BattlePassApp] 清理任務頁面');
      this.cleanupTaskPage();
      
      console.log('[BattlePassApp] 渲染獎勵軌道');
    this.renderPassLevelRewards();
      
      console.log('[BattlePassApp] 渲染任務列表');
    this.renderTaskList();
      
      console.log('[BattlePassApp] 更新用戶信息');
    this.updateUserInfo();
      
      console.log('[BattlePassApp] 更新倒計時');
    this.updateCountdown();
      
      console.log('[BattlePassApp] 更新通知點');
    this.updateNotificationDots();
      
      // 確保初始頁籤狀態正確 - 預設顯示獎勵頁面
      console.log('[BattlePassApp] 設置初始頁籤狀態');
      document.querySelectorAll('.tab-content').forEach(content => {
        content.classList.remove('active');
      });
      document.querySelectorAll('.tab-button').forEach(btn => {
        btn.classList.remove('active');
        btn.setAttribute('aria-selected', 'false');
      });
      
      // 設置獎勵頁面為預設
      const rewardTab = document.querySelector('.tab-button[aria-controls="pass-level-content"]');
      const rewardContent = document.getElementById('pass-level-content');
      if (rewardTab && rewardContent) {
        rewardTab.classList.add('active');
        rewardTab.setAttribute('aria-selected', 'true');
        rewardContent.classList.add('active');
        console.log('[BattlePassApp] 預設顯示獎勵頁面');
        
        // CSS 已自動處理初始按鈕顯示邏輯 + 添加 data 屬性作為備用
        console.log('[BattlePassApp] 初始狀態 - 顯示獎勵一鍵領取按鈕');
        
        // 添加 data 屬性確保初始按鈕正確顯示（備用方案）
        const rewardClaimBtn = document.querySelector('.pass-level-claim') as HTMLElement;
        const taskRefreshBtn = document.querySelector('.task-refresh-button') as HTMLElement;
        
        if (rewardClaimBtn) {
          rewardClaimBtn.setAttribute('data-show', 'true');
        }
        if (taskRefreshBtn) {
          taskRefreshBtn.setAttribute('data-show', 'false');
        }
      }
      
      console.log('[BattlePassApp] UI 渲染完成');
      
      // 延遲啟動多國語言功能，確保不影響主要功能
      setTimeout(() => {
        this.langSwitcher.init();
        console.log('[BattlePassApp] 多國語言功能已啟動');
      }, 1000);
    } catch (error) {
      console.error('[BattlePassApp] UI 渲染失敗：', error);
      
      // 顯示渲染錯誤
      const errorDiv = document.createElement('div');
      errorDiv.style.cssText = `
        position: fixed;
        top: 80px;
        left: 20px;
        background: red;
        color: white;
        padding: 10px;
        border-radius: 5px;
        z-index: 9999;
        font-family: monospace;
      `;
      errorDiv.textContent = `渲染錯誤: ${error}`;
      document.body.appendChild(errorDiv);
    }
  }

  private renderPassLevelRewards(): void {
    // 確保只在獎勵頁面中渲染
    const rewardsTrack = document.querySelector('#pass-level-content .bp-rewards-track') as HTMLElement;
    if (!rewardsTrack) {
      console.log('[BattlePassApp] 獎勵區域元素不存在，可能用戶在任務頁面。跳過獎勵渲染。');
      
      // 檢查是否有其他 .bp-rewards-track 元素
      const allRewardsTracks = document.querySelectorAll('.bp-rewards-track');
      console.log(`[BattlePassApp] 找到 ${allRewardsTracks.length} 個 .bp-rewards-track 元素:`, allRewardsTracks);
      
      return;
    }

    console.log('[BattlePassApp] 獎勵軌道元素:', rewardsTrack, '父容器:', rewardsTrack.parentElement);

    // 確保不會意外渲染到任務頁面
    const dailyRewardContent = document.querySelector('#daily-reward-content');
    if (dailyRewardContent) {
      // 清空任務頁面中可能存在的獎勵相關內容
      const taskRewardsTracks = dailyRewardContent.querySelectorAll('.bp-rewards-track, .reward-row, .reward-item, .level-milestone');
      taskRewardsTracks.forEach(element => {
        element.remove();
      });
      console.log(`[BattlePassApp] 從任務頁面移除了 ${taskRewardsTracks.length} 個獎勵元素`);
    }

    const userData = this.userDataManager.getData();
    const currentLevel = this.calculateUserLevel(userData.stars);
    const passLevelRewards = userData.rewards;
    
    console.log(`[BattlePassApp] 渲染獎勵軌道，當前等級: ${currentLevel}，獎勵數量: ${passLevelRewards.length}`, {
      userData: userData,
      passLevelRewards: passLevelRewards
    });
    
    let html = '';

    for (let level = 1; level <= PASS_CONFIG.MAX_LEVEL; level++) {
      const reward = passLevelRewards.find(r => r.level === level) || { level, free: null, paid: null };

      // 免費獎勵
      let freeRewardHtml = '';
      if (reward.free && reward.free.length > 0) {
        const allClaimed = reward.free.every(item => item.claimed);
        
        // 處理多個免費獎勵
        const rewardItemsHtml = reward.free.map(freeItem => {
          const iconSrc = this.getRewardIconUrl(freeItem.type, freeItem.metadata, level);
          const displayText = this.getRewardDisplayText(freeItem.type, freeItem.metadata);
          
          // 根據獎勵類型設置圖標大小
          let iconSize = 'width: 48px; height: 48px;';
          if (freeItem.type === 'Diamond') {
            iconSize = 'width: 24px; height: 24px;';
          }
          
            return `<div class="reward-item-wrapper">
                      <div class="reward-icon-container">
                        <img src="${iconSrc}" alt="${freeItem.type}" class="reward-icon-img" style="${iconSize}" onError="this.style.display='none'">
                      </div>
                      <div class="reward-text-container">
                      <span class="reward-amount">${displayText}</span>
                      </div>
                    </div>`;
        }).join('<span class="plus-icon">+</span>');

        freeRewardHtml = `
                    <div class="reward-content">${rewardItemsHtml}</div>
                    ${allClaimed ? '<span class="claimed-tick">✓</span>' : ''}
                `;
      }

      // 付費獎勵
      let paidRewardHtml = '';
      if (reward.paid && reward.paid.length > 0) {
        const isPaidAccessible = this.rewardAccessibility['MONTH_paid'] ?? false;
        const allClaimed = isPaidAccessible && reward.paid.every(item => item.claimed);
        const isLocked = !isPaidAccessible;
        
        // 處理多個付費獎勵（通常只會有一個）
        const paidItemsHtml = reward.paid.map(paidItem => {
          const iconSrc = this.getRewardIconUrl(paidItem.type, paidItem.metadata, level);
          const displayText = this.getRewardDisplayText(paidItem.type, paidItem.metadata);
          
          // 鑽石圖標通常較小
          const iconSize = paidItem.type === 'Diamond' ? 'width: 48px; height: 48px;' : 'width: 48px; height: 48px;';
          
          return `<div class="reward-item-wrapper">
                    <div class="reward-icon-container">
                      <img src="${iconSrc}" alt="${paidItem.type}" class="reward-icon-img" style="${iconSize}" onError="this.style.display='none'">
                    </div>
                    <div class="reward-text-container">
                      <span class="reward-amount large-text">${displayText}</span>
                    </div>
                  </div>`;
        }).join('<span class="plus-icon">+</span>');

        paidRewardHtml = `
                    <div class="reward-content">${paidItemsHtml}</div>
                    ${
                      allClaimed
                        ? '<span class="claimed-tick">✓</span>'
                        : isLocked
                          ? '<span class="icon lock-icon">🔒</span>'
                          : ''
                    }
                `;
      }

      // 等級里程碑
      const milestoneClass = level < currentLevel ? 'active-dot' : level === currentLevel ? 'current-dot' : '';

      // 為當前等級添加特殊樣式類別
      const currentLevelClass = level === currentLevel ? 'current-level' : '';

      html += `
                <div class="reward-row ${currentLevelClass}">
                    <div class="reward-item free-reward ${reward.free ? '' : 'empty'}" data-level="${level}">
                        ${freeRewardHtml}
                    </div>
                    <div class="level-milestone">
                        <div class="milestone-line"></div>
                        <div class="milestone-dot ${milestoneClass}">${level}</div>
                        <div class="milestone-line"></div>
                    </div>
                    <div class="reward-item swag-pass-reward" data-level="${level}">
                        ${paidRewardHtml}
                    </div>
                </div>
            `;
    }

    rewardsTrack.innerHTML = html;
    
    console.log(`[BattlePassApp] 獎勵軌道 HTML 已更新，內容長度: ${html.length}`);
    
    // 添加 Extra Bonus 區塊（500積分額外獎勵）
    this.renderExtraBonus(rewardsTrack);
    
    // 自動滾動到當前等級
    this.scrollToCurrentLevel();
  }

  /**
   * 自動滾動到用戶當前等級
   */
  private scrollToCurrentLevel(): void {
    const userData = this.userDataManager.getData();
    const currentLevel = this.calculateUserLevel(userData.stars);
    
    // 使用 requestAnimationFrame 確保 DOM 已經渲染完成
    requestAnimationFrame(() => {
      const rewardsTrack = document.querySelector('.bp-rewards-track') as HTMLElement;
      if (!rewardsTrack) {
        console.warn('[BattlePassApp] 找不到獎勵軌道容器，無法執行自動滾動');
        return;
      }

      // 找到當前等級的獎勵行
      const currentLevelRow = rewardsTrack.querySelector(`.reward-row .milestone-dot.current-dot`) as HTMLElement;
      if (!currentLevelRow) {
        // Lv.0 沒有獎勵行是正常的，不需要警告
        if (currentLevel === 0) {
          console.log(`[BattlePassApp] 等級 0 沒有獎勵行，這是正常的`);
        } else {
          console.warn(`[BattlePassApp] 找不到當前等級 ${currentLevel} 的獎勵行元素`);
        }
        return;
      }

      // 獲取當前等級行的父容器（.reward-row）
      const rewardRow = currentLevelRow.closest('.reward-row') as HTMLElement;
      if (!rewardRow) {
        console.warn('[BattlePassApp] 找不到獎勵行容器');
        return;
      }

      // 計算滾動位置：讓當前等級顯示在容器中間偏上的位置
      const containerHeight = rewardsTrack.clientHeight;
      const rowTop = rewardRow.offsetTop;
      const rowHeight = rewardRow.offsetHeight;
      
      // 目標滾動位置：讓當前等級顯示在容器的 30% 位置（偏上）
      const targetScrollTop = rowTop - (containerHeight * 0.3) + (rowHeight / 2);
      
      // 確保滾動位置在有效範圍內
      const maxScrollTop = rewardsTrack.scrollHeight - containerHeight;
      const finalScrollTop = Math.max(0, Math.min(targetScrollTop, maxScrollTop));

      console.log(`[BattlePassApp] 自動滾動到等級 ${currentLevel}`, {
        containerHeight,
        rowTop,
        rowHeight,
        targetScrollTop,
        finalScrollTop,
        maxScrollTop
      });

      // 平滑滾動到目標位置
      rewardsTrack.scrollTo({
        top: finalScrollTop,
        behavior: 'smooth'
      });
    });
  }

  private renderExtraBonus(rewardsTrack: HTMLElement): void {
    const userData = this.userDataManager.getData();
    const currentLevel = this.calculateUserLevel(userData.stars);
    
    // 先清除之前的 Extra Bonus 區塊，避免重複
    const existingExtraBonus = document.querySelector('.bp-extra-bonus');
    if (existingExtraBonus) {
      existingExtraBonus.remove();
      console.log('[BattlePassApp] 清除舊的 Extra Bonus 區塊');
    }
    
    // 1. 檢查是否滿等（60級）
    const isMaxLevel = currentLevel >= PASS_CONFIG.MAX_LEVEL;
    
    // 2. 計算滿等後的額外積分
    const maxLevelStars = PASS_CONFIG.MAX_LEVEL * PASS_CONFIG.STARS_PER_LEVEL; // 60 * 150 = 9000
    const extraStars = Math.max(0, userData.stars - maxLevelStars);
    
    // 🎯 修復：完全基於 API 數據計算額外獎勵狀態
    const availableBonuses = Math.floor(extraStars / PASS_CONFIG.EXTRA_BONUS_POINTS);
    
    // 從 API 獎勵數據中計算已領取次數
    const passLevelRewards = userData.rewards;
    const extraRewardLevels = passLevelRewards.filter(reward => reward.level > PASS_CONFIG.MAX_LEVEL);
    let claimedBonuses = 0;
    
    extraRewardLevels.forEach(reward => {
      const allItemsClaimed = [
        ...(reward.free || []),
        ...(reward.paid || [])
      ].every(item => item.claimed);
      
      if (allItemsClaimed) {
        claimedBonuses++;
      }
    });
    
    // 如果沒有找到額外獎勵等級數據，使用保守估算
    if (extraRewardLevels.length === 0 && availableBonuses > 0) {
      const currentExtraStars = extraStars % PASS_CONFIG.EXTRA_BONUS_POINTS;
      if (currentExtraStars === 0 && extraStars > 0) {
        claimedBonuses = availableBonuses; // 假設已全部領取
      } else {
        claimedBonuses = availableBonuses - 1; // 假設還有一次未領取
      }
    }
    
    const unclaimedBonuses = Math.max(0, availableBonuses - claimedBonuses);
    
    console.log(`[renderExtraBonus] 🎯 API額外獎勵狀態: 可獲得=${availableBonuses}, API已領取=${claimedBonuses}, 未領取=${unclaimedBonuses}`);
    
    // 4. 計算下次獎勵需要的積分
    const nextBonusStars = maxLevelStars + ((claimedBonuses + 1) * PASS_CONFIG.EXTRA_BONUS_POINTS);
    const starsNeeded = Math.max(0, nextBonusStars - userData.stars);
    
    console.log(`[Extra Bonus] 滿等: ${isMaxLevel}, 額外積分: ${extraStars}, 可領取: ${availableBonuses}, 已領取: ${claimedBonuses}, 未領取: ${unclaimedBonuses}`);
    
    let statusText = '';
    let statusClass = '';
    
    if (!isMaxLevel) {
      statusText = this.simpleI18n.translate('需達到') + ` ${PASS_CONFIG.MAX_LEVEL} ` + this.simpleI18n.translate('級');
      statusClass = 'status-locked';
    } else if (unclaimedBonuses > 0) {
      statusText = this.simpleI18n.translate('可領取') + ` ${unclaimedBonuses} ` + this.simpleI18n.translate('次');
      statusClass = 'status-available';
    } else if (starsNeeded > 0) {
      statusText = this.simpleI18n.translate('還需') + ` ${starsNeeded} ` + this.simpleI18n.translate('積分');
      statusClass = 'status-progress';
    } else {
      statusText = this.simpleI18n.translate('已達成條件');
      statusClass = 'status-ready';
    }
    
    const extraBonusHtml = `
      <div class="bp-extra-bonus">
        <h2 class="extra-bonus-title">${this.simpleI18n.translate('額外獎勵')}</h2>
        <p class="extra-bonus-description">${this.simpleI18n.translate('滿等後，每累積 500 積分可獲得下列獎勵')}</p>
        <div class="extra-reward-display">
          <img src="https://public.swag.live/products/pass_nu_livestream_pass_10m.png" alt="直播暢遊券" class="reward-icon-img" onError="this.style.display='none'">
          <span class="reward-amount">${this.simpleI18n.translate('10分鐘直播暢遊券')}</span>
          <span class="plus-icon">+</span>
          <img src="https://public.swag.live/products/pass_cn_flix_pass_7d.png" alt="長片觀看券" class="reward-icon-img" onError="this.style.display='none'">
          <span class="reward-amount">${this.simpleI18n.translate('10分鐘長片觀看券')}</span>
        </div>
        <div class="extra-bonus-status ${statusClass}">
          <span class="status-text">${statusText}</span>
        </div>
        ${claimedBonuses > 0 ? `<div class="extra-bonus-history">${this.simpleI18n.translate('已領取次數')} ${claimedBonuses}</div>` : ''}
        ${unclaimedBonuses > 0 ? `<div class="claim-instruction">${this.simpleI18n.translate('請使用上方「一鍵領取」按鈕領取')}</div>` : ''}
      </div>
    `;
    
    rewardsTrack.insertAdjacentHTML('beforeend', extraBonusHtml);
    
    // 🎯 移除額外獎勵按鈕事件監聽，統一使用「一鍵領取」按鈕
    
    console.log('[BattlePassApp] Extra Bonus 區塊已添加');
  }

  private claimExtraBonus(claimCount: number): void {
    const userData = this.userDataManager.getData();
    const currentClaimedBonuses = userData.claimedExtraBonuses || 0;
    
    // 更新已領取次數
    this.userDataManager.updateData({
      claimedExtraBonuses: currentClaimedBonuses + claimCount
    });
    
    this.showSuccess(`成功領取 ${claimCount} 次額外獎勵！獲得 ${claimCount} 張直播暢遊券和 ${claimCount} 張長片觀看券`);
    
    // 重新渲染獎勵區域
    this.renderPassLevelRewards();
  }

  private renderTaskList(): void {
    const taskList = document.querySelector('#daily-reward-content .task-list');
    if (!taskList) {
      console.error('[BattlePassApp] 找不到 #daily-reward-content .task-list 元素');
      
      // 檢查是否有其他 .task-list 元素
      const allTaskLists = document.querySelectorAll('.task-list');
      console.log(`[BattlePassApp] 找到 ${allTaskLists.length} 個 .task-list 元素:`, allTaskLists);
      
      return;
    }

    console.log('[BattlePassApp] 任務列表元素:', taskList, '父容器:', taskList.parentElement);

    // 強制清空任務頁面中的所有獎勵相關內容
    const dailyRewardContent = document.querySelector('#daily-reward-content');
    if (dailyRewardContent) {
      // 移除所有獎勵相關元素
      const rewardElements = dailyRewardContent.querySelectorAll(
        '.bp-rewards-track, .rewards-header, .reward-row, .reward-item, .level-milestone, .milestone-dot, .reward-content'
      );
      rewardElements.forEach(element => {
        element.remove();
      });
      console.log(`[BattlePassApp] 從任務頁面強制移除了 ${rewardElements.length} 個獎勵元素`);
      
      // 確保只有任務容器可見
      const children = Array.from(dailyRewardContent.children);
      children.forEach(child => {
        if (!child.classList.contains('daily-tasks-wrapper')) {
          child.remove();
          console.log('[BattlePassApp] 移除非任務容器元素:', child);
        }
      });
    }

    const tasks = this.userDataManager.getData().tasks || [];
    console.log(`[BattlePassApp] 渲染任務列表，任務數量: ${tasks.length}`, tasks);
    
    // 🔄 自動給分模式：對任務進行排序 - 進行中 > 已領取（已完成）
    const sortedTasks = tasks.sort((a, b) => {
      // 檢查任務是否已完成（有 completed 時間戳）
      const isCompletedA = typeof a.completed === 'number';
      const isCompletedB = typeof b.completed === 'number';
      
      // 定義任務狀態的優先級（自動給分模式）
      const getStatusPriority = (task: Task) => {
        const isCompleted = typeof task.completed === 'number';
        if (isCompleted) {
          return 2; // 已完成（已領取）- 最低優先級，移到下方
        } else {
          return 1; // 進行中 - 最高優先級，顯示在上方
        }
      };
      
      const priorityA = getStatusPriority(a);
      const priorityB = getStatusPriority(b);
      
      console.log(`🔧 [排序] 任務 ${a.id}: completed=${a.completed}, priority=${priorityA}`);
      console.log(`🔧 [排序] 任務 ${b.id}: completed=${b.completed}, priority=${priorityB}`);
      
      // 按優先級排序
      if (priorityA !== priorityB) {
        return priorityA - priorityB;
      }
      
      // 同優先級內按 id 排序
      return a.id.localeCompare(b.id);
    });
    
    let html = '';

    sortedTasks.forEach((task: Task) => {
      const progressPercentage = Math.min((task.progress.current / task.progress.target) * 100, 100);
      const isCompleted = task.status === TASK_STATUS.COMPLETED;

      // 🔧 [修復] 恢復三種按鈕狀態：前往、領取獎勵、已完成
      // 🔄 自動給分模式：只有「前往」和「已完成」兩個狀態
      let buttonText = this.simpleI18n.translate('前往');
      let buttonClass = 'go-button goto';
      
      if (isCompleted) {
        // 任務完成後自動給分，顯示「已完成」
        buttonText = this.simpleI18n.translate('已完成');
        buttonClass = 'go-button claimed';
      }
      
      console.log(`🔧 [跨設備修復] 任務 ${task.id}: status=${task.status}, buttonText=${buttonText}`);

      html += `
                <li class="task-item" data-task-id="${task.id}">
                    <div class="task-info">
                        <div class="task-description">${this.simpleI18n.translate(task.description)}</div>
                        <div class="task-reward">⭐ ${task.reward}</div>
                            </div>
                    <div class="task-action-area">
                        <button class="${buttonClass}" ${isCompleted ? 'disabled' : ''} data-task-link="${task.link || ''}">${buttonText}</button>
                    </div>
                </li>
            `;
    });

    taskList.innerHTML = html;
    
    console.log(`[BattlePassApp] 任務列表 HTML 已更新，內容長度: ${html.length}`);
  }

  private updateUserInfo(): void {
    const userData = this.userDataManager.getData();
    const currentLevel = this.calculateUserLevel(userData.stars);

    this.userDataManager.updateData({ level: currentLevel });
    this.updateProgressBar(userData.stars);

    // 更新 DOM 狀態屬性
    this.updateAppStatus('ready');
  }

  private updateProgressBar(currentStars: number): void {
    const progressText = document.querySelector('.progress-text');
    const progressBar = document.querySelector('.progress-bar') as HTMLElement;
    const levelIndicator = document.querySelector('.level-indicator');

    if (progressText && progressBar && levelIndicator) {
      const currentLevel = this.calculateUserLevel(currentStars);
      
      // 🔧 修復：顯示當前等級內的積分進度
      const currentLevelStars = currentStars % PASS_CONFIG.STARS_PER_LEVEL;
      const progressPercentage = (currentLevelStars / PASS_CONFIG.STARS_PER_LEVEL) * 100;

      // 顯示當前等級內的積分/150 (例如: 85/150 Lv.40)
      progressText.textContent = `${currentLevelStars}/${PASS_CONFIG.STARS_PER_LEVEL}`;
      progressBar.style.width = `${progressPercentage}%`;
      progressBar.setAttribute('aria-valuenow', progressPercentage.toString());
      levelIndicator.textContent = `Lv.${Math.min(currentLevel, PASS_CONFIG.MAX_LEVEL)}`;
      
      console.log(`🔧 [DEBUG] updateProgressBar: ${currentLevelStars}/${PASS_CONFIG.STARS_PER_LEVEL}, Lv.${currentLevel}, ${progressPercentage.toFixed(1)}% (總積分: ${currentStars})`);
    }
  }

  private updateCountdown(): void {
    const updateTimer = () => {
      const now = new Date();
      // 判斷重置時間：如果是當月1號且未到08:00，重置時間是今天08:00；否則是下個月1號08:00
      let nextReset;
      if (now.getDate() === 1 && now.getHours() < 8) {
        nextReset = new Date(now.getFullYear(), now.getMonth(), 1, 8, 0, 0);
      } else {
        nextReset = new Date(now.getFullYear(), now.getMonth() + 1, 1, 8, 0, 0);
      }
      const timeLeft = nextReset.getTime() - now.getTime();

      if (timeLeft <= 0) {
        document.querySelector('.countdown-timer')!.innerHTML = '00d 00h 00m';
        return;
      }

      const days = Math.floor(timeLeft / (1000 * 60 * 60 * 24));
      const hours = Math.floor((timeLeft % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      const minutes = Math.floor((timeLeft % (1000 * 60 * 60)) / (1000 * 60));

      const daysElement = document.querySelector('.days');
      const hoursElement = document.querySelector('.hours');
      const minutesElement = document.querySelector('.minutes');

      if (daysElement) daysElement.textContent = days.toString().padStart(2, '0');
      if (hoursElement) hoursElement.textContent = hours.toString().padStart(2, '0');
      if (minutesElement) minutesElement.textContent = minutes.toString().padStart(2, '0');
    };

    updateTimer();
    setInterval(updateTimer, 60000); // 每分鐘更新一次
  }

  private calculateUserLevel(stars: number): number {
    console.log('🔧 [DEBUG] calculateUserLevel 輸入:', stars, typeof stars);
    
    // 防禦性編程：檢查輸入值
    if (typeof stars !== 'number' || isNaN(stars) || stars < 0) {
      console.warn('🔧 [WARN] calculateUserLevel 收到異常值:', stars, '使用預設值 0');
      return 0;
    }
    
    const level = Math.min(Math.floor(stars / PASS_CONFIG.STARS_PER_LEVEL), PASS_CONFIG.MAX_LEVEL);
    console.log('🔧 [DEBUG] calculateUserLevel 結果:', level, `(${stars} ÷ ${PASS_CONFIG.STARS_PER_LEVEL} = ${stars / PASS_CONFIG.STARS_PER_LEVEL})`);
    return level;
  }

  /**
   * 計算已領取任務的積分總和
   * 只計算狀態為 CLAIMED 的任務積分
   */
  private calculateClaimedTasksPoints(tasks: Task[]): number {
    return tasks
      .filter(task => task.status === TASK_STATUS.CLAIMED)
      .reduce((total, task) => total + task.reward, 0);
  }

  /**
   * 從 localStorage 獲取已領取的任務 ID 列表
   */
  private getClaimedTaskIds(): Set<string> {
    try {
      const claimedIds = localStorage.getItem('battlepass_claimed_tasks');
      return claimedIds ? new Set(JSON.parse(claimedIds)) : new Set();
    } catch (error) {
      if (shouldLog('warn')) {
        console.warn('[BattlePassApp] 無法讀取已領取任務記錄:', error);
      }
      return new Set();
    }
  }

  /**
   * 將已領取的任務 ID 保存到 localStorage
   */
  private saveClaimedTaskId(taskId: string): void {
    try {
      const claimedIds = this.getClaimedTaskIds();
      claimedIds.add(taskId);
      localStorage.setItem('battlepass_claimed_tasks', JSON.stringify([...claimedIds]));
      
      if (shouldLog('info')) {
        console.log(`[BattlePassApp] 保存已領取任務: ${taskId}`);
      }
    } catch (error) {
      if (shouldLog('warn')) {
        console.warn('[BattlePassApp] 無法保存已領取任務記錄:', error);
      }
    }
  }

  /**
   * 根據等級和獎勵金額返回正確的鑽石圖示
   * @param level - 通行證等級
   * @param rewardAmount - 獎勵金額
   */
  private getDiamondIcon(level: number, rewardAmount: number): string {
    const baseUrl = config.assets.iconBaseUrl;

    // 等級 1：使用 Diamond_3.svg
    if (level === 1) {
      return `${baseUrl}img-Diamond_3.svg`;
    }

    // 等級 60：使用 Diamond_5.svg
    if (level === 60) {
      return `${baseUrl}img-Diamond_5.svg`;
    }

    // 等級 2-59 的獎勵規則
    if (rewardAmount === 1000) {
      return `${baseUrl}img-Diamond_4.svg`;
    } else if (rewardAmount >= 200) {
      return `${baseUrl}img-Diamond_2.svg`;
    } else {
      return `${baseUrl}img-Diamond_1.svg`;
    }
  }

  /**
   * Process API reward metadata and add frontend-specific information
   * @param rewardType - 獎勵類型
   * @param apiMetadata - API 回傳的原始元數據
   * @param level - 通行證等級
   * @param apiItem - 完整的 API item 物件（包含可能在 item 層級的欄位）
   */
  private processRewardMetadata(rewardType: string, apiMetadata: any, level: number = 0, apiItem?: any): any {
    const processedMetadata = { ...apiMetadata };
    
    // Convert duration from seconds to minutes for display
    // 先檢查 item 層級的 duration（真實 API），再檢查 metadata 層級（Mock API）
    const duration = apiItem?.duration || apiMetadata.duration;
    if (duration) {
      processedMetadata.duration = duration;
      processedMetadata.durationMinutes = Math.round(duration / 60);
    }
    
    // Add frontend-specific fields
    processedMetadata.iconUrl = this.getRewardIconUrl(rewardType, processedMetadata, level);
    processedMetadata.displayText = this.getRewardDisplayText(rewardType, processedMetadata);
    
    return processedMetadata;
  }

  /**
   * 根據獎勵類型和元數據返回正確的圖示URL
   * @param rewardType - 獎勵類型
   * @param metadata - 獎勵元數據
   * @param level - 通行證等級（用於鑽石圖示選擇）
   */
  private getRewardIconUrl(rewardType: string, metadata: any, level: number = 0): string {
    const baseUrl = config.assets.iconBaseUrl;
    
    switch (rewardType) {
      case 'Diamond':
        const amount = typeof metadata.value === 'number' ? metadata.value : 
                      typeof metadata.amount === 'number' ? metadata.amount : 0;
        return this.getDiamondIcon(level, amount);
      
      case 'LivestreamPass':
        // 直播暢遊券圖示
        return 'https://public.swag.live/products/pass_nu_livestream_pass_10m.png';
      
      case 'MessageVoucher':
      case 'FlixFeedPass':
        // 長片觀看券圖示（支援新舊類型名稱）
        return 'https://public.swag.live/products/pass_cn_flix_pass_7d.png';
      
      case 'ticket_bundle':
        // 根據 metadata 判斷是哪種券，或使用預設的直播暢遊券
        if (metadata && metadata.items && metadata.items.length > 0) {
          const firstItem = metadata.items[0];
          if (firstItem.type === 'MessageVoucher' || firstItem.type === 'FlixFeedPass') {
            return 'https://public.swag.live/products/pass_cn_flix_pass_7d.png';
          }
        }
        return 'https://public.swag.live/products/pass_nu_livestream_pass_10m.png';
      
      default:
        // 預設使用直播暢遊券圖示
        return 'https://public.swag.live/products/pass_nu_livestream_pass_10m.png';
    }
  }

  /**
   * 根據獎勵類型獲取顯示文本
   * @param rewardType - 獎勵類型
   * @param apiMetadata - API 原始元數據
   */
  private getRewardDisplayText(rewardType: string, apiMetadata: any): string {
    switch (rewardType) {
      case 'Diamond':
        const amount = apiMetadata.value || apiMetadata.amount || 0;
        return amount.toLocaleString();
      
      case 'LivestreamPass':
        // Convert seconds to minutes for display
        const minutes = apiMetadata.duration ? Math.round(apiMetadata.duration / 60) : 0;
        return this.simpleI18n.translate(`${minutes}分鐘直播暢遊券`);
      
      case 'MessageVoucher':
        // Convert seconds to minutes for display
        const minutes2 = apiMetadata.duration ? Math.round(apiMetadata.duration / 60) : 0;
        return this.simpleI18n.translate(`${minutes2}分鐘長片觀看券`);
      
      case 'FlixFeedPass':
        // Convert seconds to minutes for display - 長片觀看券的新類型
        const minutes3 = apiMetadata.duration ? Math.round(apiMetadata.duration / 60) : 0;
        return this.simpleI18n.translate(`${minutes3}分鐘長片觀看券`);
      
      default:
        // Handle unknown reward types with potential translation
        const defaultMinutes = apiMetadata.duration ? Math.round(apiMetadata.duration / 60) : 0;
        const value = apiMetadata.value || apiMetadata.amount || '';
        
        if (defaultMinutes > 0) {
          // For duration-based rewards, try to determine type or fallback to generic
          // First try direct translation of specific types
          const livestreamText = this.simpleI18n.translate(`${defaultMinutes}分鐘直播暢遊券`);
          const videoText = this.simpleI18n.translate(`${defaultMinutes}分鐘長片觀看券`);
          
          // If we have specific translations, prefer livestream as default
          if (livestreamText !== `${defaultMinutes}分鐘直播暢遊券`) {
            return livestreamText;
          } else if (videoText !== `${defaultMinutes}分鐘長片觀看券`) {
            return videoText;
          }
          
          // Fallback to showing just the duration if no specific translation exists
          return `${defaultMinutes}分鐘`;
        }
        
        return value ? value.toString() : '';
    }
  }

  /**
   * 根據獎勵類型和元數據返回正確的圖示URL (向後兼容)
   * @param rewardType - 獎勵類型
   * @param metadata - 獎勵元數據
   * @param level - 通行證等級（用於鑽石圖示選擇）
   */
  private getRewardIcon(rewardType: string, metadata: any, level: number = 0): string {
    return this.getRewardIconUrl(rewardType, metadata, level);
  }

  private async claimReward(rewardElement: HTMLElement, level: number): Promise<void> {
    const userData = this.userDataManager.getData();
    const currentLevel = this.calculateUserLevel(userData.stars);

    if (currentLevel === 0 || level > currentLevel) {
      this.showError(this.simpleI18n.translate('等級不足，無法領取此獎勵'));
      return;
    }

    const isFreeReward = rewardElement.classList.contains('free-reward');
    const passLevelRewards = this.userDataManager.getData().rewards;
    const rewardForLevel = passLevelRewards.find(r => r.level === level);

    if (!this.battlePassService) {
      this.showError(this.simpleI18n.translate('服務未初始化，請重新載入頁面'));
      return;
    }

    try {
      // 顯示載入狀態
      rewardElement.style.opacity = '0.6';
      rewardElement.style.pointerEvents = 'none';

    if (isFreeReward) {
      if (rewardForLevel?.free && rewardForLevel.free.every(item => item.claimed)) {
        this.showError(this.simpleI18n.translate('此獎勵已經領取過了'));
        return;
      }

        // 收集要領取的免費獎勵 ID
        const rewardIds: string[] = [];
      if (rewardForLevel?.free) {
          rewardForLevel.free.forEach(freeItem => {
            if (!freeItem.claimed && freeItem.id && typeof freeItem.id === 'string') {
              rewardIds.push(freeItem.id);
            }
          });
        }

        if (rewardIds.length > 0) {
          // 調用真實 API
          const response = await this.battlePassService.claimRewards({
            group: "MONTH",
            reward_ids: rewardIds,
          });

          // 根據 API 回應更新本地狀態
          if ('claimed_ids' in response) {
            // 成功回應
            const claimedIds = response.claimed_ids;
        let claimedCount = 0;
            
            if (rewardForLevel?.free) {
        rewardForLevel.free.forEach(freeItem => {
                if (claimedIds.includes(freeItem.id)) {
            freeItem.claimed = true;
            claimedCount++;
          }
        });
            }
            
        if (claimedCount > 0) {
          this.showSuccess(`成功領取 ${claimedCount} 個免費獎勵！`);
            }
          } else {
            // 部分成功回應
            this.showWarning(this.simpleI18n.translate('部分獎勵領取成功，請檢查獎勵狀態'));
        }
      }
    } else {
        // 檢查付費獎勵是否可訪問
        const isPaidAccessible = this.rewardAccessibility['MONTH_paid'] ?? false;
        if (!isPaidAccessible) {
        this.showPurchaseDialog();
        return;
      }

      if (rewardForLevel?.paid && rewardForLevel.paid.every(item => item.claimed)) {
        this.showError(this.simpleI18n.translate('此獎勵已經領取過了'));
        return;
      }

        // 收集要領取的付費獎勵 ID
        const rewardIds: string[] = [];
      if (rewardForLevel?.paid) {
          rewardForLevel.paid.forEach(paidItem => {
            if (!paidItem.claimed && paidItem.id && typeof paidItem.id === 'string') {
              rewardIds.push(paidItem.id);
            }
          });
        }

        if (rewardIds.length > 0) {
          // 調用真實 API
          const response = await this.battlePassService.claimRewards({
            group: "MONTH",
            reward_ids: rewardIds,
          });

          // 根據 API 回應更新本地狀態
          if ('claimed_ids' in response) {
            // 成功回應
            const claimedIds = response.claimed_ids;
        let totalDiamonds = 0;
        let claimedCount = 0;
            
            if (rewardForLevel?.paid) {
        rewardForLevel.paid.forEach(paidItem => {
                if (claimedIds.includes(paidItem.id)) {
            paidItem.claimed = true;
                  if (typeof paidItem.metadata.value === 'number') {
            totalDiamonds += paidItem.metadata.value as number;
                  }
            claimedCount++;
          }
        });
            }
        
        if (claimedCount > 0) {
          this.userDataManager.updateData({
            diamonds: userData.diamonds + totalDiamonds,
          });
          this.showSuccess(`成功領取 ${totalDiamonds.toLocaleString()} 鑽石！`);
            }
          } else {
            // 部分成功回應
            this.showWarning(this.simpleI18n.translate('部分獎勵領取成功，請檢查獎勵狀態'));
        }
      }
    }

      // 更新本地數據並重新渲染
    this.userDataManager.updateData({ rewards: passLevelRewards });
    this.renderPassLevelRewards();
    this.updateNotificationDots();

    } catch (error) {
      console.error('[BattlePassApp] 領取獎勵失敗:', error);
      this.showError(`領取獎勵失敗: ${error instanceof Error ? error.message : '未知錯誤'}`);
    } finally {
      // 恢復按鈕狀態
      rewardElement.style.opacity = '1';
      rewardElement.style.pointerEvents = 'auto';
    }
  }

  private async claimAllPassRewards(): Promise<void> {
    if (!this.battlePassService) {
      this.showError(this.simpleI18n.translate('服務未初始化，請重新載入頁面'));
      return;
    }

    const userData = this.userDataManager.getData();
    const currentLevel = this.calculateUserLevel(userData.stars);
    const passLevelRewards = userData.rewards;

    // Lv 0 用戶不能領取任何獎勵
    if (currentLevel === 0) {
      this.showError(this.simpleI18n.translate('等級不足，請先完成任務提升等級'));
      return;
    }

    try {
      // 顯示載入狀態
      const claimButton = document.querySelector('.pass-level-claim') as HTMLElement;
      const buttonText = claimButton?.querySelector('.button-text');
      let originalButtonText = '';
      
      if (claimButton) {
        claimButton.style.opacity = '0.6';
        claimButton.style.pointerEvents = 'none';
        
        if (buttonText) {
          originalButtonText = buttonText.textContent || this.simpleI18n.translate('一鍵領取');
          buttonText.textContent = this.simpleI18n.translate('領取中...');
          
          // 為英文 CLAIMING... 添加特殊樣式以避免跑版
          if (this.simpleI18n.translate('領取中...') === 'CLAIMING...') {
            buttonText.classList.add('claiming-text');
          }
        }
      }

      // 🔧 修復：直接從 API 數據收集可領取獎勵
      const freeRewardIds: string[] = [];
      const paidRewardIds: string[] = [];
      let isPaidAccessible = false;

      try {
        // 重新獲取最新的 API 獎勵數據
        const [monthlyFreeRewards, monthlyPaidRewards] = await Promise.all([
          this.battlePassService!.getRewards('MONTH', 'free'),
          this.battlePassService!.getRewards('MONTH', 'paid'),
        ]);

        // 收集免費獎勵 - 基於用戶積分和 target 值
        monthlyFreeRewards.rewards.forEach(reward => {
          if (userData.stars >= reward.target && !reward.claimed && reward.id) {
            freeRewardIds.push(reward.id);
          }
        });

        // 收集付費獎勵 - 需要檢查 Pass 訪問權限
        isPaidAccessible = monthlyPaidRewards.is_accessible ?? false;
        if (isPaidAccessible) {
          monthlyPaidRewards.rewards.forEach(reward => {
            if (userData.stars >= reward.target && !reward.claimed && reward.id) {
              paidRewardIds.push(reward.id);
            }
          });
        }

        console.log('🔧 [獎勵修復] 可領取免費獎勵:', freeRewardIds.length, '個');
        console.log('🔧 [獎勵修復] 可領取付費獎勵:', paidRewardIds.length, '個');
        console.log('🔧 [獎勵修復] 用戶積分:', userData.stars, 'Pass 權限:', isPaidAccessible);
        
        // 🔍 詳細調試：列出所有獎勵 ID 和驗證
        console.log('🔍 [調試] 免費獎勵 IDs:', freeRewardIds);
        console.log('🔍 [調試] 付費獎勵 IDs:', paidRewardIds);
        console.log('🔍 [調試] 免費獎勵 ID 長度檢查:', freeRewardIds.map(id => `${id} (${id.length})`));
        console.log('🔍 [調試] 付費獎勵 ID 長度檢查:', paidRewardIds.map(id => `${id} (${id.length})`));
        
        // 檢查 API 限制
        if (freeRewardIds.length > 30) {
          console.warn('⚠️ [API限制] 免費獎勵 ID 數量超過 30:', freeRewardIds.length);
        }
        if (paidRewardIds.length > 30) {
          console.warn('⚠️ [API限制] 付費獎勵 ID 數量超過 30:', paidRewardIds.length);
        }

      } catch (error) {
        console.error('🔧 [獎勵修復] 獲取獎勵數據失敗:', error);
        this.showError(this.simpleI18n.translate('獎勵領取失敗，請稍後再試'));
      return;
    }

      if (freeRewardIds.length === 0 && paidRewardIds.length === 0) {
        this.showInfo(this.simpleI18n.translate('沒有可領取的獎勵'));
        return;
      }

      // 🔧 修復：支援超過 30 個獎勵的分批處理
      const batchClaimRewards = async (rewardIds: string[], type: string): Promise<{claimedIds: string[], hasErrors: boolean}> => {
        const BATCH_SIZE = 30; // API 限制每次最多 30 個
        const batches = [];
        
        // 將獎勵 ID 分割成批次
        for (let i = 0; i < rewardIds.length; i += BATCH_SIZE) {
          batches.push(rewardIds.slice(i, i + BATCH_SIZE));
        }
        
        console.log(`🔧 [分批處理] ${type}獎勵總數: ${rewardIds.length}, 分成 ${batches.length} 批次`);
        
        const allClaimedIds: string[] = [];
        let hasErrors = false;
        
        // 依序處理每個批次
        for (let i = 0; i < batches.length; i++) {
          const batch = batches[i];
          console.log(`🔧 [分批處理] 處理第 ${i + 1}/${batches.length} 批次，包含 ${batch.length} 個獎勵`);
          
          try {
            const response = await this.battlePassService!.claimRewards({
              group: "MONTH",
              reward_ids: batch,
            });

            if ('claimed_ids' in response) {
              allClaimedIds.push(...response.claimed_ids);
              console.log(`✅ [分批處理] 第 ${i + 1} 批次成功: ${response.claimed_ids.length} 個獎勵`);
            } else if ('succeed_reward_ids' in response) {
              allClaimedIds.push(...response.succeed_reward_ids);
              console.warn(`⚠️ [分批處理] 第 ${i + 1} 批次部分成功:`, response);
              hasErrors = true;
            }
            
            // 批次間短暫延遲，避免 API 限流
            if (i < batches.length - 1) {
              console.log(`🔧 [分批處理] 等待 500ms 後處理下一批次...`);
              await new Promise(resolve => setTimeout(resolve, 500));
            }
            
          } catch (error) {
            console.error(`❌ [分批處理] 第 ${i + 1} 批次失敗:`, error);
            hasErrors = true;
          }
        }
        
        console.log(`🔧 [分批處理] ${type}獎勵處理完成: 成功 ${allClaimedIds.length} 個，錯誤: ${hasErrors}`);
        return { claimedIds: allClaimedIds, hasErrors };
      };

      // 開始分別領取獎勵
      let allClaimedIds: string[] = [];
      let hasErrors = false;

      // 先領取免費獎勵（分批處理）
      if (freeRewardIds.length > 0) {
        console.log('🔧 [獎勵修復] 開始領取免費獎勵:', freeRewardIds.length, '個');
        const freeResult = await batchClaimRewards(freeRewardIds, '免費');
        allClaimedIds.push(...freeResult.claimedIds);
        hasErrors = hasErrors || freeResult.hasErrors;
      }

      // 再領取付費獎勵（分批處理，如果有權限）
      if (paidRewardIds.length > 0 && isPaidAccessible) {
        console.log('🔧 [獎勵修復] 開始領取付費獎勵:', paidRewardIds.length, '個');
        const paidResult = await batchClaimRewards(paidRewardIds, '付費');
        allClaimedIds.push(...paidResult.claimedIds);
        hasErrors = hasErrors || paidResult.hasErrors;
      }

      // 處理最終結果
      if (allClaimedIds.length === 0) {
        this.showError(this.simpleI18n.translate(hasErrors ? '獎勵領取失敗，請稍後再試' : '沒有成功領取任何獎勵'));
        return;
      }

      // 使用合併的成功領取 ID 來更新狀態
      const response = { claimed_ids: allClaimedIds };

      // 根據 API 回應更新本地狀態
      if ('claimed_ids' in response) {
        // 成功回應
        const claimedIds = response.claimed_ids;
        let claimedCount = 0;
        let totalDiamonds = 0;

        for (let level = 1; level <= currentLevel; level++) {
          const reward = passLevelRewards.find(r => r.level === level);
          
          // 更新免費獎勵狀態
          if (reward?.free) {
            reward.free.forEach(freeItem => {
              if (claimedIds.includes(freeItem.id)) {
                freeItem.claimed = true;
                claimedCount++;
              }
            });
          }
          
          // 更新付費獎勵狀態
          if (reward?.paid && isPaidAccessible) {
            reward.paid.forEach(paidItem => {
              if (claimedIds.includes(paidItem.id)) {
                paidItem.claimed = true;
                if (typeof paidItem.metadata.value === 'number') {
                  totalDiamonds += paidItem.metadata.value as number;
                }
                claimedCount++;
              }
            });
          }
        }

        if (claimedCount > 0) {
      this.userDataManager.updateData({
            rewards: passLevelRewards,
            diamonds: userData.diamonds + totalDiamonds,
          });
          
          let message = `成功領取 ${claimedCount} 個獎勵！`;
          if (totalDiamonds > 0) {
            message += ` 獲得 ${totalDiamonds.toLocaleString()} 鑽石！`;
          }
          this.showSuccess(message);
        }
      } else {
        // 部分成功回應
        this.showWarning(this.simpleI18n.translate('部分獎勵領取成功，請檢查獎勵狀態'));
      }

      // 🎯 關鍵修復：領取成功後執行軟重新載入（與更新進度按鈕相同邏輯）
      console.log('🔄 [視覺更新] 開始軟重新載入...');
      
      // 🎯 移除額外獎勵的 localStorage 更新，現在完全依賴 API 數據
      console.log(`🎯 [額外獎勵] 不再使用 localStorage，完全依賴 API 數據狀態`);
      
      // 給後端一點時間處理數據同步
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // 🔄 執行軟重新載入（與更新進度按鈕相同的邏輯）
      await this.performSoftReload();
      
      console.log('✅ [視覺更新] 軟重新載入完成');

    } catch (error) {
      console.error('[BattlePassApp] 一鍵領取獎勵失敗:', error);
      this.showError(`一鍵領取失敗: ${error instanceof Error ? error.message : '未知錯誤'}`);
    } finally {
      // 恢復按鈕狀態
      const claimButton = document.querySelector('.pass-level-claim') as HTMLElement;
      const buttonText = claimButton?.querySelector('.button-text');
      
      if (claimButton) {
        claimButton.style.opacity = '1';
        claimButton.style.pointerEvents = 'auto';
        
        // 恢復按鈕文字（如果數據重載後還有可領取獎勵，會自動更新）
        if (buttonText && buttonText.textContent === this.simpleI18n.translate('領取中...')) {
          buttonText.textContent = this.simpleI18n.translate('一鍵領取');
          // 移除 CLAIMING 特殊樣式
          buttonText.classList.remove('claiming-text');
        }
      }
    }
  }

  // 🔄 自動給分模式：移除任務一鍵領取功能（已由後端自動處理）

  /**
   * 軟重新載入 - 清除本地狀態後重新載入數據（適用於 iframe 環境）
   * 避免破壞 iframe 認證，同時確保數據完全同步
   */
  private async performSoftReload(): Promise<void> {
    console.log('[BattlePassApp] 🔄 執行軟重新載入...');
    
    // 1. 清除 Battle Pass 相關的本地數據（保留認證相關）
    // 🎯 移除 claimedExtraBonuses，現在完全依賴 API 數據
    const keysToRemove = Object.keys(localStorage).filter(key => 
      key.includes('battlepass') || 
      key.includes('rewards') ||
      key.includes('tasks')
    );
    keysToRemove.forEach(key => localStorage.removeItem(key));
    console.log('[BattlePassApp] 🔄 清除本地緩存數據');
    
    // 2. 重新載入 API 數據
    console.log('[BattlePassApp] 🔄 重新載入 API 數據...');
    await this.loadApiData();
    
    // 3. 重新渲染整個 UI
    console.log('[BattlePassApp] 🔄 重新渲染 UI...');
    this.renderUI();
    
    // 4. 確保翻譯正確應用
    setTimeout(() => {
      this.simpleI18n.updatePageTexts();
      console.log('[BattlePassApp] 🔄 軟重載後翻譯已更新');
    }, 100);
    
    console.log('[BattlePassApp] ✅ 軟重新載入完成');
  }

  /**
   * 更新任務進度 - 軟重新載入（適用於 iframe 環境）
   * 清除本地狀態後重新載入 API 數據，避免破壞 iframe 認證
   */
  private async refreshTaskProgress(): Promise<void> {
    // 獲取更新進度按鈕
    const refreshButton = document.querySelector('.task-refresh-button') as HTMLElement;
    if (!refreshButton) return;

    try {
      // 顯示 loading 狀態
      refreshButton.innerHTML = `<span class="button-text">${this.simpleI18n.translate('更新中...')}</span>`;
      (refreshButton as HTMLButtonElement).disabled = true;
      refreshButton.style.opacity = '0.6';
      refreshButton.style.pointerEvents = 'none';

      console.log('[BattlePassApp] 🔄 任務更新進度開始...');
      
      // 重置語言切換按鈕到預設位置
      if (this.langSwitcher) {
        this.langSwitcher.resetToDefaultPosition();
        console.log('[BattlePassApp] 🔄 語言切換按鈕已重置到預設位置');
      }
      
      // 執行軟重新載入
      await this.performSoftReload();
      
      // 切換回任務頁面
      console.log('[BattlePassApp] 🔄 恢復任務頁面狀態...');
      const taskButton = document.querySelector('.tab-button[aria-controls="daily-reward-content"]') as HTMLElement;
      if (taskButton) {
        this.handleTabSwitch(taskButton);
      }

    } catch (error) {
      console.error('[BattlePassApp] 任務更新進度失敗:', error);
      this.showError(this.simpleI18n.translate('更新進度失敗，請稍後再試'));
    } finally {
      // 恢復按鈕狀態
      const currentRefreshButton = document.querySelector('.task-refresh-button') as HTMLElement;
      if (currentRefreshButton) {
        currentRefreshButton.innerHTML = `<span class="button-text">${this.simpleI18n.translate('更新進度')}</span>`;
        (currentRefreshButton as HTMLButtonElement).disabled = false;
        currentRefreshButton.style.opacity = '1';
        currentRefreshButton.style.pointerEvents = 'auto';
      }
    }
  }

  private showPurchaseDialog(): void {
    // 檢查是否已經購買過 Battle Pass
    const isPaidAccessible = this.rewardAccessibility['MONTH_paid'] ?? false;
    
    if (isPaidAccessible) {
      // 如果已經購買過，顯示提示訊息
      this.showAlreadyPurchasedDialog();
      return;
    }

    // 如果未購買過，顯示購買對話框
    const modal = document.createElement('div');
    modal.className = 'modal-overlay';
    modal.innerHTML = `
            <div class="modal-content">
                <div class="modal-header">
                    <h2>${this.simpleI18n.translate('購買 Battle Pass')}</h2>
                    <button class="modal-close">&times;</button>
                </div>
                <div class="modal-body">
                    <p style="white-space: pre-line;">${this.simpleI18n.translate('20,000 鑽 價值 NT$ 1499 ，6折優惠帶走！')}</p>
                    <div class="price">${this.simpleI18n.translate('NT$ 599')}</div>
                </div>
                <div class="modal-footer">
                    <button class="btn-cancel">${this.simpleI18n.translate('取消')}</button>
                    <button class="btn-purchase">${this.simpleI18n.translate('購買')}</button>
                </div>
            </div>
        `;

    document.body.appendChild(modal);

    // 事件處理
    modal.querySelector('.modal-close')?.addEventListener('click', () => {
      modal.remove();
    });

    modal.querySelector('.btn-cancel')?.addEventListener('click', () => {
      modal.remove();
    });

    modal.querySelector('.btn-purchase')?.addEventListener('click', () => {
      // 在新視窗開啟 SWAG 購買頁面
      const purchaseUrl = 'https://swag.live/?campaign-id=checkout-diamondpack&campaign=eyJzZWN0aW9ucyI6WyJzZWN0aW9uPWJhdHRsZV9wYXNzLXNob3Atc2VjdGlvbiJdfQ==';
      
      try {
        console.log(`[BattlePassApp] 開啟 Battle Pass 購買頁面: ${purchaseUrl}`);
        
        // 顯示跳轉提示
        this.showInfo('正在前往購買頁面...');
        
        // 在新視窗開啟購買頁面
        window.open(purchaseUrl, '_blank', 'noopener,noreferrer');
        
        // 關閉購買對話框
      modal.remove();
        
      } catch (error) {
        console.error('[BattlePassApp] 開啟購買頁面失敗:', error);
        this.showError(this.simpleI18n.translate('無法開啟購買頁面，請手動前往 SWAG 官網購買'));
      }
    });

    modal.addEventListener('click', e => {
      if (e.target === modal) {
        modal.remove();
      }
    });
  }

  private showAlreadyPurchasedDialog(): void {
    const modal = document.createElement('div');
    modal.className = 'modal-overlay';
    modal.innerHTML = `
            <div class="modal-content">
                <div class="modal-header">
                    <h2>${this.simpleI18n.translate('您已經購買過 Battle Pass！')}</h2>
                    <button class="modal-close">&times;</button>
                </div>
                <div class="modal-body">
                    <p>${this.simpleI18n.translate('本期 Battle Pass 您已經擁有了，無需重複購買。')}</p>
                </div>
                <div class="modal-footer">
                    <button class="btn-confirm">${this.simpleI18n.translate('確定')}</button>
                </div>
            </div>
        `;

    document.body.appendChild(modal);

    // 事件處理
    const closeModal = () => modal.remove();
    
    modal.querySelector('.modal-close')?.addEventListener('click', closeModal);
    modal.querySelector('.btn-confirm')?.addEventListener('click', closeModal);
    modal.addEventListener('click', e => {
      if (e.target === modal) {
        closeModal();
      }
    });
  }

  private updateNotificationDots(): void {
    const userData = this.userDataManager.getData();
    const currentLevel = this.calculateUserLevel(userData.stars);
    const passLevelRewards = userData.rewards;

    // 獎勵頁面通知點
    let passRewardCount = 0;
    for (let level = 1; level <= currentLevel; level++) {
      const rewardForLevel = passLevelRewards.find(r => r.level === level);

      if (rewardForLevel?.free && rewardForLevel.free.some(item => !item.claimed)) {
        passRewardCount++;
      }

      const isPaidAccessible = this.rewardAccessibility['MONTH_paid'] ?? false;
      if (isPaidAccessible && rewardForLevel?.paid && rewardForLevel.paid.some(item => !item.claimed)) {
        passRewardCount++;
      }
    }

    // 🎯 修復：完全基於 API 數據計算額外獎勵數量
    let unclaimedExtraBonuses = 0;
    
    try {
      // 從 API 獎勵數據中直接計算額外獎勵狀態
      const allApiRewards = passLevelRewards.flatMap(reward => [
        ...(reward.free || []),
        ...(reward.paid || [])
      ]);
      
      // 找到所有額外獎勵（假設額外獎勵的 level 為 0 或特殊標識）
      // 由於前端數據結構限制，我們用積分範圍來判斷額外獎勵
      const maxLevelStars = PASS_CONFIG.MAX_LEVEL * PASS_CONFIG.STARS_PER_LEVEL; // 9000
      const extraStars = Math.max(0, userData.stars - maxLevelStars);
      const theoreticalExtraBonuses = Math.floor(extraStars / PASS_CONFIG.EXTRA_BONUS_POINTS);
      
      // 從獎勵數據中找到可能的額外獎勵項目（等級 > 60 的獎勵）
      const extraRewardLevels = passLevelRewards.filter(reward => reward.level > PASS_CONFIG.MAX_LEVEL);
      let claimedExtraCount = 0;
      
      extraRewardLevels.forEach(reward => {
        const allItemsClaimed = [
          ...(reward.free || []),
          ...(reward.paid || [])
        ].every(item => item.claimed);
        
        if (allItemsClaimed) {
          claimedExtraCount++;
        }
      });
      
      // 如果沒有找到額外獎勵等級數據，使用保守估算
      if (extraRewardLevels.length === 0 && theoreticalExtraBonuses > 0) {
        // 基於用戶積分，如果積分剛好是 500 的倍數，說明可能已經領取完畢
        const currentExtraStars = extraStars % PASS_CONFIG.EXTRA_BONUS_POINTS;
        if (currentExtraStars === 0 && extraStars > 0) {
          // 積分是 500 的整數倍，且有額外積分，假設已全部領取
          unclaimedExtraBonuses = 0;
        } else {
          // 還有未達到 500 的積分，計算未領取次數
          unclaimedExtraBonuses = Math.max(0, theoreticalExtraBonuses - Math.floor(extraStars / PASS_CONFIG.EXTRA_BONUS_POINTS));
        }
      } else {
        unclaimedExtraBonuses = Math.max(0, theoreticalExtraBonuses - claimedExtraCount);
      }
      
      console.log(`[updateNotificationDots] 🎯 API額外獎勵計算: 積分=${userData.stars}, 額外積分=${extraStars}, 理論次數=${theoreticalExtraBonuses}, API已領取=${claimedExtraCount}, 未領取=${unclaimedExtraBonuses}`);
      
    } catch (error) {
      console.warn('[updateNotificationDots] 額外獎勵計算失敗，使用預設值 0:', error);
      unclaimedExtraBonuses = 0;
    }
    
    // 將額外獎勵數量加入總計
    passRewardCount += unclaimedExtraBonuses;
    
    console.log(`[updateNotificationDots] 等級獎勵: ${passRewardCount - unclaimedExtraBonuses}, 額外獎勵: ${unclaimedExtraBonuses}, 總計: ${passRewardCount}`);

    // 任務頁面通知點 - 智能顯示邏輯
    const tasks = userData.tasks || [];
    const completedTaskCount = tasks.filter(task => typeof task.completed === 'number').length;
    const totalTaskCount = tasks.length;
    
    // 用戶邏輯：部分完成時顯示，全部完成或全部未完成時不顯示
    let shouldShowTaskNotification = false;
    if (completedTaskCount > 0 && completedTaskCount < totalTaskCount) {
      shouldShowTaskNotification = true;
    }
    
    console.log(`[updateNotificationDots] 任務通知邏輯: 已完成=${completedTaskCount}/${totalTaskCount}, 顯示通知=${shouldShowTaskNotification}`);
    
    const taskRewardCount = shouldShowTaskNotification ? 1 : 0;

    // 更新通知點
    const rewardTab = document.querySelector(
      '.tab-button[aria-controls="pass-level-content"] .notification-dot'
    ) as HTMLElement;
    const taskTab = document.querySelector(
      '.tab-button[aria-controls="daily-reward-content"] .notification-dot'
    ) as HTMLElement;

    if (rewardTab) {
      rewardTab.style.display = passRewardCount > 0 ? 'block' : 'none';
    }

    if (taskTab) {
      taskTab.style.display = taskRewardCount > 0 ? 'block' : 'none';
    }

    // 🔄 自動給分模式：只更新獎勵一鍵領取按鈕
    const passClaimBtn = document.querySelector('.pass-level-claim .claim-count');

    if (passClaimBtn) {
      passClaimBtn.textContent = passRewardCount.toString();
      // 🎯 修復：按鈕永遠顯示，只更新數字，不隱藏按鈕
      (passClaimBtn.parentElement as HTMLElement).style.display = 'flex';
    }
  }

  private showError(message: string): void {
    this.showNotification(message, 'error');
  }

  private showSuccess(message: string): void {
    this.showNotification(message, 'success');
  }

  private showWarning(message: string): void {
    this.showNotification(message, 'warning');
  }

  private showInfo(message: string): void {
    this.showNotification(message, 'info');
  }

  /**
   * 更新彈窗中的版本號
   */
  private updateModalVersion(version: string): void {
    const versionElement = document.getElementById('modal-version');
    if (versionElement) {
      versionElement.textContent = `v${version}`;
      console.log(`[BattlePassApp] 版本號已更新: v${version}`);
    } else {
      console.warn('[BattlePassApp] 找不到版本號元素 #modal-version');
    }
  }

  private showNotification(message: string, type: 'error' | 'success' | 'warning' | 'info'): void {
    if (shouldLog('info')) {
      console.log(`[${type.toUpperCase()}] ${message}`);
    }

    // 移除現有的通知（避免堆疊）
    const existingNotifications = document.querySelectorAll('.notification');
    existingNotifications.forEach(notif => notif.remove());

    const notification = document.createElement('div');
    notification.className = `notification notification-${type}`;
    notification.textContent = message;
    
    // 添加關閉按鈕
    const closeButton = document.createElement('button');
    closeButton.className = 'notification-close';
    closeButton.innerHTML = '×';
    closeButton.onclick = () => notification.remove();
    notification.appendChild(closeButton);

    document.body.appendChild(notification);

    // 自動消失時間根據訊息類型調整
    const autoHideTime = type === 'error' ? 8000 : type === 'success' ? 3000 : 5000;
    setTimeout(() => {
      if (notification.parentNode) {
      notification.remove();
      }
    }, autoHideTime);
  }

  private addModeIndicator(): void {
    // 只在嵌入模式時不顯示指示器
    if (this.isEmbeddedMode) return;

    // 只在開發環境下顯示指示器
    const isDevelopment = config.environment === 'development';
    if (!isDevelopment) return;

    const indicator = document.createElement('div');
    indicator.className = 'dev-indicator';
    indicator.textContent = 'Dev';
    indicator.title = `Development Mode - Mock Server: ${config.api.baseUrl}`;

    // 小而低調的樣式
    indicator.style.cssText = `
      position: fixed;
      bottom: 20px;
      right: 20px;
      background: #2196F3;
      color: white;
      padding: 4px 8px;
      border-radius: 12px;
      font-size: 10px;
      z-index: 1000;
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
      font-weight: 500;
      box-shadow: 0 2px 4px rgba(0,0,0,0.1);
      opacity: 0.7;
      transition: opacity 0.2s ease;
      cursor: default;
      user-select: none;
    `;

    // 滑鼠懸停時稍微突出
    indicator.addEventListener('mouseenter', () => {
      indicator.style.opacity = '1';
    });

    indicator.addEventListener('mouseleave', () => {
      indicator.style.opacity = '0.7';
    });

    document.body.appendChild(indicator);
  }

  private updateAppStatus(status: string): void {
    const mainElement = document.querySelector('.battle-pass-screen');
    if (mainElement) {
      mainElement.setAttribute('data-app-status', status);
      mainElement.setAttribute('data-app-mode', this.isEmbeddedMode ? 'embedded' : 'standalone');
      mainElement.setAttribute('data-is-authenticated', this.authManager.isAuthenticated().toString());

      const userData = this.userDataManager.getData();
      mainElement.setAttribute('data-user-stars', userData.stars.toString());
      mainElement.setAttribute('data-user-level', userData.level.toString());
      mainElement.setAttribute('data-tasks-count', userData.tasks.length.toString());

      if (shouldLog('debug')) {
        console.log(
          `[BattlePassApp] 狀態更新: ${status}, 模式: ${this.isEmbeddedMode ? '嵌入' : 'Standalone'}, 認證: ${this.authManager.isAuthenticated()}`
        );
      }
    } else {
      if (shouldLog('debug')) {
        console.log(`[BattlePassApp] DOM 元素尚未就緒，延遲狀態更新: ${status}`);
      }
      setTimeout(() => this.updateAppStatus(status), 100);
    }
  }

  private cleanupTaskPage(): void {
    const dailyRewardContent = document.querySelector('#daily-reward-content');
    if (!dailyRewardContent) return;

    console.log('[BattlePassApp] 開始清理任務頁面...');

    // 移除所有可能的獎勵相關元素
    const rewardSelectors = [
      '.bp-rewards-track',
      '.rewards-header', 
      '.reward-row',
      '.reward-item',
      '.level-milestone',
      '.milestone-dot',
      '.milestone-line',
      '.reward-content',
      '.reward-icon-img',
      '.reward-amount',
      '.reward-icon-container',
      '.reward-text-container',
      '.reward-item-wrapper',
      '.claimed-tick',
      '.lock-icon',
      '.plus-icon',
      '.multiplier-badge'
    ];

    let removedCount = 0;
    rewardSelectors.forEach(selector => {
      const elements = dailyRewardContent.querySelectorAll(selector);
      elements.forEach(element => {
        element.remove();
        removedCount++;
      });
    });

    console.log(`[BattlePassApp] 任務頁面清理完成，移除了 ${removedCount} 個獎勵相關元素`);

    // 確保任務容器存在且可見
    let tasksWrapper = dailyRewardContent.querySelector('.daily-tasks-wrapper');
    if (!tasksWrapper) {
      tasksWrapper = document.createElement('div');
      tasksWrapper.className = 'daily-tasks-wrapper';
      tasksWrapper.innerHTML = '<ul class="task-list" role="list"></ul>';
      dailyRewardContent.appendChild(tasksWrapper);
      console.log('[BattlePassApp] 重新創建任務容器');
    }
  }

  // 暫時註解掉所有多國語言相關方法
  /*
  private async initializeI18n(): Promise<void> {
    try {
      console.log('[BattlePassApp] 開始初始化多國語言系統');
      await this.i18nManager.initialize();
      this.setupLanguageSwitcher();
      this.updateAllTexts();
      console.log('[BattlePassApp] 多國語言系統初始化完成');
    } catch (error) {
      console.error('[BattlePassApp] 多國語言系統初始化失敗:', error);
    }
  }

  private setupLanguageSwitcher(): void {
    const container = document.getElementById('language-switcher-container');
    if (container) {
      const switcher = this.languageSwitcher.create();
      container.appendChild(switcher);
      
      // 監聽語言變更
      this.i18nManager.addLanguageChangeListener(() => {
        this.updateAllTexts();
      });
      
      console.log('[BattlePassApp] 語言切換器已設置');
    } else {
      console.warn('[BattlePassApp] 找不到語言切換器容器');
    }
  }

  private updateAllTexts(): void {
    // 更新所有帶有 data-i18n 屬性的元素
    const elements = document.querySelectorAll('[data-i18n]');
    elements.forEach(element => {
      const key = element.getAttribute('data-i18n');
      if (key) {
        const text = this.i18nManager.getText(key);
        element.textContent = text;
      }
    });

    // 更新動態生成的內容
    this.updateDynamicTexts();
    
    console.log(`[BattlePassApp] 已更新 ${elements.length} 個文本元素`);
  }

  private updateDynamicTexts(): void {
    // 更新倒計時單位
    this.updateCountdownTexts();
    
    // 暫時註解掉重新渲染，避免無限循環
    // if (this.isInitialized) {
    //   this.renderTaskList();
    //   this.renderPassLevelRewards();
    // }
  }

  private updateCountdownTexts(): void {
    const daysSpan = document.querySelector('.countdown-timer [data-i18n="header.countdown.days"]');
    const hoursSpan = document.querySelector('.countdown-timer [data-i18n="header.countdown.hours"]');
    const minutesSpan = document.querySelector('.countdown-timer [data-i18n="header.countdown.minutes"]');

    if (daysSpan) daysSpan.textContent = this.i18nManager.getText('header.countdown.days');
    if (hoursSpan) hoursSpan.textContent = this.i18nManager.getText('header.countdown.hours');
    if (minutesSpan) minutesSpan.textContent = this.i18nManager.getText('header.countdown.minutes');
  }
  */
}

// 當 DOM 載入完成時初始化應用程式
document.addEventListener('DOMContentLoaded', () => {
  try {
    console.log('[BattlePassApp] DOM 載入完成，開始初始化應用程式');
  new BattlePassApp();
  } catch (error) {
    console.error('[BattlePassApp] 應用程式初始化失敗：', error);
    
    // 顯示基本的錯誤信息
    const errorDiv = document.createElement('div');
    errorDiv.style.cssText = `
      position: fixed;
      top: 20px;
      left: 20px;
      background: red;
      color: white;
      padding: 10px;
      border-radius: 5px;
      z-index: 9999;
      font-family: monospace;
    `;
    errorDiv.textContent = `初始化錯誤: ${error}`;
    document.body.appendChild(errorDiv);
  }
});

// 導出給其他模組使用
export { BattlePassApp };
